import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAgents } from '../hooks/useAgents';
import { Box, Stack, Button, Typography, IconButton, Divider, Tooltip, Paper, Badge, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Menu, MenuItem, ListItemIcon, FormControl, InputLabel, Select, Card, CardContent, CardActions, CircularProgress, Alert, Checkbox, FormControlLabel, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import QuizIcon from '@mui/icons-material/Quiz';
import ChatIcon from '@mui/icons-material/Chat';
import ImageIcon from '@mui/icons-material/Image';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useBuilder } from './BuilderContext';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
// import CodeIcon from '@mui/icons-material/Code';
import TuneIcon from '@mui/icons-material/Tune';

// Get API base URL from environment variable
const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

const providerHasModels = (provider) => !!(provider?.available ?? (provider?.configured && Array.isArray(provider?.models) && provider.models.length > 0));
const providerMenuLabel = (provider) => `${provider.name}${!provider.configured ? ' (not configured)' : (!providerHasModels(provider) ? ' (unavailable)' : '')}`;

// MVP scaffold shell
const ExperimentBuilder = () => {
  const {
    state,
    addBlock,
    removeBlock,
    updateBlock,
    addTrial,
    removeTrial,
    updateTrial,
    navigate,
    setOrientation,
    computeHash,
    reorderBlocks,
    reorderBlockChildren,
    // Variant APIs used when editing variant views
    addVariantTrial,
    updateVariantBlock,
    removeVariantBlock,
    updateVariantTrial,
    removeVariantTrial,
    // reorderVariantBlocks
  } = useBuilder();
  // Auth is used in subcomponents; not needed here
  const { draft, ui, validation } = state;
  const [promptEditor, setPromptEditor] = React.useState(null); // { scope, blockId, trialId, variantId? }
  const tokens = usePromptTokens();
  const currentVariant = draft.variants.find(v => v.id === ui.selectedVariantId) || null;
  const inVariantFlow = !!currentVariant;
  const currentBlockSource = inVariantFlow ? currentVariant?.blocks : draft.blocks;
  const currentBlock = (currentBlockSource||[]).find(b => b.id === ui.selectedBlockId) || null;
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [hash, setHash] = React.useState('');
  const [importOpen, setImportOpen] = React.useState(false);
  // Meta editing handled in ExperimentLevel; no local state here
  // Remember the last block type the user explicitly chose (improves keyboard flow)
  const [lastBlockType, setLastBlockType] = React.useState(() => localStorage.getItem('builder:lastBlockType') || 'conversation');

  const rememberAndAddBlock = React.useCallback((type) => {
    setLastBlockType(type);
    try { localStorage.setItem('builder:lastBlockType', type); } catch(e) { /* ignore storage errors */ }
    addBlock(type);
  }, [addBlock]);

  const openPreview = async () => {
    const h = await computeHash();
    setHash(h);
    setPreviewOpen(true);
  };
  const openPublish = async () => {
    const h = await computeHash();
    setHash(h);
    setPublishOpen(true);
  };

  const blockTrials = () => {
    if (!currentBlock) return [];
    if (currentBlock.type === 'conversation') return currentBlock.trials || [];
    if (currentBlock.type === 'questionnaire' || currentBlock.type === 'scale') return currentBlock.questions || [];
    if (currentBlock.type === 'stimuli') return currentBlock.stimuli || [];
    if (currentBlock.type === 'primed_conversation') return currentBlock.stimuli || [];
    return [];
  };

  const orientationIcon = ui.orientation === 'vertical' ? <ViewWeekIcon /> : <ViewStreamIcon />;

  // helper to fetch items for any block type without short-circuiting on empty arrays
  const getBlockItems = React.useCallback((blk) => {
    if (!blk) return [];
    if (blk.type === 'conversation') return blk.trials || [];
    if (blk.type === 'questionnaire' || blk.type === 'scale') return blk.questions || [];
    if (blk.type === 'stimuli') return blk.stimuli || [];
    if (blk.type === 'primed_conversation') return blk.stimuli || [];
    return [];
  }, []);

  const goUp = () => {
    if (ui.level === 'trial') navigate({ level: (currentVariant? 'variant':'block'), selectedTrialId: null });
    else if (ui.level === 'block' && currentVariant) navigate({ level: 'variant', selectedBlockId: null, selectedTrialId:null });
    else if (ui.level === 'block' && !currentVariant) navigate({ level: 'experiment', selectedBlockId: null });
    else if (ui.level === 'variant') navigate({ level: 'experiment', selectedVariantId: null, selectedBlockId:null, selectedTrialId:null });
  };

  const openBlock = (id) => navigate({ level: 'block', selectedBlockId: id, selectedTrialId: null });
  const openTrial = (id) => navigate({ level: 'trial', selectedTrialId: id });

  // keyboard navigation (no conditions yet; reserved for future counterbalancing)
  React.useEffect(()=>{
    const listener = (e)=>{
      const { level, selectedBlockId, selectedTrialId } = state.ui;
      if (['INPUT','TEXTAREA'].includes(e.target.tagName)) return; // don't override typing
      if (e.key === 'ArrowDown') {
        if (level === 'experiment' && draft.blocks.length){
          navigate({ level:'block', selectedBlockId: draft.blocks[0].id, selectedTrialId:null }); e.preventDefault();
        } else if (level === 'block') {
          const blk = draft.blocks.find(b=>b.id===selectedBlockId); if (blk){
            const items = blk.trials||blk.questions||blk.stimuli||[]; if (items.length){ navigate({ level:'trial', selectedTrialId: items[0].id }); e.preventDefault(); }
          }
        }
      } else if (e.key === 'ArrowUp') {
        if (level === 'trial') { navigate({ level:'block', selectedTrialId:null }); e.preventDefault(); }
        else if (level === 'block') { navigate({ level:'experiment', selectedBlockId:null }); e.preventDefault(); }
      } else if (e.key === 'ArrowRight') {
        if (level === 'block') {
          const idx = draft.blocks.findIndex(b=>b.id===selectedBlockId); if (idx>=0 && idx<draft.blocks.length-1){ navigate({ level:'block', selectedBlockId:draft.blocks[idx+1].id, selectedTrialId:null }); e.preventDefault(); }
        } else if (level === 'trial') {
          const blk = draft.blocks.find(b=>b.id===selectedBlockId); if (blk){
            const items = blk.trials||blk.questions||blk.stimuli||[]; const i = items.findIndex(t=>t.id===selectedTrialId); if (i>=0 && i<items.length-1){ navigate({ level:'trial', selectedTrialId: items[i+1].id }); e.preventDefault(); }
          }
        }
      } else if (e.key === 'ArrowLeft') {
        if (level === 'block') {
          const idx = draft.blocks.findIndex(b=>b.id===selectedBlockId); if (idx>0){ navigate({ level:'block', selectedBlockId:draft.blocks[idx-1].id, selectedTrialId:null }); e.preventDefault(); }
        } else if (level === 'trial') {
          const blk = draft.blocks.find(b=>b.id===selectedBlockId); if (blk){
            const items = blk.trials||blk.questions||blk.stimuli||[]; const i = items.findIndex(t=>t.id===selectedTrialId); if (i>0){ navigate({ level:'trial', selectedTrialId: items[i-1].id }); e.preventDefault(); }
          }
        }
      } else if (e.key === 'Delete') {
        if (level === 'block' && selectedBlockId){ removeBlock(selectedBlockId); e.preventDefault(); }
        else if (level === 'trial' && selectedBlockId && selectedTrialId){ removeTrial(selectedBlockId, selectedTrialId); navigate({ level:'block', selectedTrialId:null }); e.preventDefault(); }
      } else if (e.code === 'Space') {
        if (level === 'experiment') { 
          addBlock(lastBlockType || 'conversation'); 
          e.preventDefault(); 
        } else if (level === 'block' && selectedBlockId) { 
          const blk = draft.blocks.find(b=>b.id===selectedBlockId);
          if (blk && blk.type === 'scale') {
            // Disable adding trials/questions inside scale blocks via spacebar
            e.preventDefault();
          } else if (blk) {
            addTrial(selectedBlockId); 
            e.preventDefault();
          }
        }
      }
    };
    window.addEventListener('keydown', listener);
    return ()=> window.removeEventListener('keydown', listener);
  }, [state.ui, draft.blocks, navigate, addBlock, addTrial, removeBlock, removeTrial, lastBlockType]);

  const header = (
    <Box display="flex" alignItems="center" gap={1} mb={1}>
      {(ui.level !== 'experiment') && (
        <Tooltip title="Up one level">
          <IconButton onClick={goUp} size="small"><ArrowBackIcon /></IconButton>
        </Tooltip>
      )}
      <Typography variant="h6" flexGrow={1} sx={{ maxWidth:140, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{draft.meta.title}</Typography>
      <Tooltip title="Toggle orientation">
        <IconButton onClick={() => setOrientation(ui.orientation === 'vertical' ? 'horizontal' : 'vertical')} size="small">{orientationIcon}</IconButton>
      </Tooltip>
      <Tooltip title="Preview JSON & Hash">
        <span>
          <IconButton size="small" onClick={openPreview}><VisibilityIcon fontSize="small" /></IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Import JSON">
        <span>
          <IconButton size="small" onClick={()=> setImportOpen(true)}><UploadFileIcon fontSize="small" /></IconButton>
        </span>
      </Tooltip>
      <Tooltip title={validation.errors.length ? 'Resolve errors before publish' : 'Publish (stub)'}>
        <span>
          <IconButton size="small" disabled={validation.errors.length>0} onClick={openPublish}><PublishIcon fontSize="small" /></IconButton>
        </span>
      </Tooltip>
      <PublishBadge validation={validation} />
    </Box>
  );

  // Unified drag handler (blocks vs children)
  const isDraggingRef = React.useRef(false);
  const onDragStart = () => { isDraggingRef.current = true; };
  const onDragEnd = (result) => {
    // delay clearing dragging flag until after potential navigation side-effects
    setTimeout(()=>{ isDraggingRef.current = false; }, 0);
    if (!result.destination) return;
    const { source, destination, type } = result;
    if (type === 'BLOCK') {
      const items = Array.from(draft.blocks);
      const [moved] = items.splice(source.index,1);
      items.splice(destination.index,0,moved);
      reorderBlocks(items);
      return;
    }
    if (type.startsWith('BLOCK_CHILD_')) {
      const blockId = type.replace('BLOCK_CHILD_','');
      const blk = draft.blocks.find(b=>b.id===blockId);
      if (!blk) return;
      const items = getBlockItems(blk).slice();
      const [moved] = items.splice(source.index,1);
      items.splice(destination.index,0,moved);
      // we need specific field name; reuse reorderBlockChildren via dynamic list
      reorderBlockChildren(blockId, items);
    }
  };

  return (
  <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
    <Stack direction={ui.orientation === 'vertical' ? 'row' : 'column'} spacing={2} sx={{ height: '80vh' }}>
      <Paper sx={{ p: 1, width: ui.orientation === 'vertical' ? 300 : '100%', flexShrink: 0, overflow: 'auto' }}>
        {header}
        <Divider sx={{ mb: 1 }} />
        {ui.level === 'experiment' && draft.variants.length > 0 && (
          <VariantList variants={draft.variants} currentVariantId={ui.selectedVariantId} onSelect={(vid)=> navigate({ level:'variant', selectedVariantId: vid, selectedBlockId:null, selectedTrialId:null })} />
        )}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{currentVariant ? `Variant Blocks (${currentVariant.label})` : 'Structure'}</Typography>
        <Stack spacing={0.5} sx={{ userSelect:'none' }}>
          {(currentBlockSource||[]).map((b, idx) => {
            const hasError = validation.errors.some(e => e.blockId === b.id);
            const hasWarn = validation.warnings.some(w => w.blockId === b.id);
            const activeBlock = b.id === ui.selectedBlockId;
            const trials = getBlockItems(b);
            return (
              <Box key={b.id}>
                <Paper onClick={() => openBlock(b.id)} sx={{ p: 0.75, mb:0.25, cursor:'pointer', bgcolor: activeBlock ? 'action.selected' : undefined, border: '1px solid', borderColor: hasError ? 'error.main' : hasWarn ? 'warning.main' : 'divider' }}>
                  <Typography variant="body2" sx={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontWeight: activeBlock ? 600 : 400 }}>{idx+1}. {b.title || `(${b.type})`}</Typography>
                </Paper>
                {activeBlock && trials.length>0 && (
                  <Stack spacing={0.25} sx={{ pl:2, mb:0.5 }}>
                    {trials.map((t,i)=>{
                      const activeTrial = ui.level==='trial' && ui.selectedTrialId===t.id;
                      return (
                        <Paper key={t.id} onClick={()=> openTrial(t.id)} sx={{ p:0.5, cursor:'pointer', bgcolor: activeTrial ? 'primary.light' : 'background.paper', border:'1px dashed', borderColor: activeTrial ? 'primary.main' : 'divider' }}>
                          <Typography variant="caption" sx={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{i+1}. {t.text || t.label || t.agentRef || '(item)'}</Typography>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            );
          })}
          {!currentVariant && <AddBlockButton onAdd={rememberAndAddBlock} size="small" sx={{ mt:1 }} />}
          {currentVariant && <Typography variant="caption" color="text.secondary">Base frozen; variant editing coming soon (read-only).</Typography>}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <ValidationSummary validation={validation} />
      </Paper>

      <Paper sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
  {ui.level === 'experiment' && <ExperimentLevel dragType="BLOCK" isDraggingRef={isDraggingRef} onAddBlock={rememberAndAddBlock} />}
  {ui.level === 'variant' && currentVariant && (
    <VariantLevel variant={currentVariant} />
  )}
        {ui.level === 'block' && currentBlock && !inVariantFlow && (
          <BlockLevel block={currentBlock} dragType={`BLOCK_CHILD_${currentBlock.id}`} isDraggingRef={isDraggingRef} onAddTrial={() => addTrial(currentBlock.id)} onOpenTrial={openTrial} onUpdateBlock={patch => updateBlock(currentBlock.id, patch)} onRemoveBlock={() => removeBlock(currentBlock.id)} trials={blockTrials()} openPromptEditor={(p)=> setPromptEditor(p)} />
        )}
        {ui.level === 'block' && currentBlock && inVariantFlow && currentVariant && (
          <BlockLevel
            block={currentBlock}
            dragType={`BLOCK_CHILD_${currentBlock.id}`}
            isDraggingRef={isDraggingRef}
            onAddTrial={() => addVariantTrial(currentVariant.id, currentBlock.id)}
            onOpenTrial={openTrial}
            onUpdateBlock={patch => updateVariantBlock(currentVariant.id, currentBlock.id, patch)}
            onRemoveBlock={() => removeVariantBlock(currentVariant.id, currentBlock.id)}
            trials={blockTrials()}
            variantMode
            variantId={currentVariant.id}
            openPromptEditor={(p)=> setPromptEditor({ ...p, variantId: currentVariant.id })}
          />
        )}
        {ui.level === 'trial' && currentBlock && !inVariantFlow && <TrialLevel block={currentBlock} trials={blockTrials()} openPromptEditor={(p)=> setPromptEditor(p)} />}
        {ui.level === 'trial' && currentBlock && inVariantFlow && currentVariant && (
          <TrialLevel
            block={currentBlock}
            trials={blockTrials()}
            variantMode
            variantId={currentVariant.id}
            updateVariantTrial={updateVariantTrial}
            removeVariantTrial={removeVariantTrial}
            openPromptEditor={(p)=> setPromptEditor({ ...p, variantId: currentVariant.id })}
          />
        )}
      </Paper>
      <PreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} hash={hash} />
      <PublishDialog open={publishOpen} onClose={() => setPublishOpen(false)} hash={hash} validation={validation} />
      <ImportDialog open={importOpen} onClose={()=> setImportOpen(false)} />
      <PromptBuilderDialog
        open={!!promptEditor}
        onClose={()=> setPromptEditor(null)}
        tokens={tokens}
        initialDescription={(function(){
          if (!promptEditor) return '';
          const variant = promptEditor.variantId ? draft.variants.find(v=> v.id===promptEditor.variantId) : null;
          const blocksArr = variant ? variant.blocks : draft.blocks;
          const blk = blocksArr.find(b=> b.id===promptEditor.blockId);
          if (!blk) return '';
          if (promptEditor.scope === 'conversation_trial') {
            const t = (blk.trials||[]).find(t=> t.id===promptEditor.trialId);
            return t?.task_description || '';
          }
          if (promptEditor.scope === 'primed_block') {
            const t = (blk.conversation?.trials||[]).find(t=> t.id===promptEditor.trialId);
            return t?.task_description || '';
          }
          if (promptEditor.scope === 'primed_stimulus') {
            const s = (blk.stimuli||[]).find(s=> s.id===promptEditor.trialId);
            return s?.conversation?.task_description || '';
          }
          return '';
        })()}
        initialOutput={(function(){
          if (!promptEditor) return '';
          const variant = promptEditor.variantId ? draft.variants.find(v=> v.id===promptEditor.variantId) : null;
          const blocksArr = variant ? variant.blocks : draft.blocks;
          const blk = blocksArr.find(b=> b.id===promptEditor.blockId);
          if (!blk) return '';
          if (promptEditor.scope === 'conversation_trial') {
            const t = (blk.trials||[]).find(t=> t.id===promptEditor.trialId);
            return t?.expected_output || '';
          }
          if (promptEditor.scope === 'primed_block') {
            const t = (blk.conversation?.trials||[]).find(t=> t.id===promptEditor.trialId);
            return t?.expected_output || '';
          }
          if (promptEditor.scope === 'primed_stimulus') {
            const s = (blk.stimuli||[]).find(s=> s.id===promptEditor.trialId);
            return s?.conversation?.expected_output || '';
          }
          return '';
        })()}
        onSave={(desc, out)=>{
          if (!promptEditor) return;
          const { scope, blockId, trialId, variantId } = promptEditor;
          if (scope === 'conversation_trial') {
            if (variantId) updateVariantTrial(variantId, blockId, trialId, { task_description: desc, expected_output: out });
            else updateTrial(blockId, trialId, { task_description: desc, expected_output: out });
          } else if (scope === 'primed_block') {
            const variant = variantId ? draft.variants.find(v=> v.id===variantId) : null;
            const blocksArr = variant ? variant.blocks : draft.blocks;
            const blk = blocksArr.find(b=> b.id===blockId);
            if (blk) {
              const trials = (blk.conversation?.trials||[]).map(t=> t.id===trialId? { ...t, task_description: desc, expected_output: out }: t);
              if (variantId) updateVariantBlock(variantId, blockId, { conversation: { ...(blk.conversation||{}), trials } });
              else updateBlock(blockId, { conversation: { ...(blk.conversation||{}), trials } });
            }
          } else if (scope === 'primed_stimulus') {
            const variant = variantId ? draft.variants.find(v=> v.id===variantId) : null;
            const blocksArr = variant ? variant.blocks : draft.blocks;
            const blk = blocksArr.find(b=> b.id===blockId);
            const stim = blk?.stimuli?.find(s=> s.id===trialId);
            const conv = stim?.conversation || { engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 } };
            const patch = { conversation: { ...conv, task_description: desc, expected_output: out } };
            if (variantId) updateVariantTrial(variantId, blockId, trialId, patch);
            else updateTrial(blockId, trialId, patch);
          }
          setPromptEditor(null);
        }}
      />
    </Stack>
    </DragDropContext>
  );
};

const PublishBadge = ({ validation }) => {
  const hasErrors = validation.errors.length > 0;
  const color = hasErrors ? 'error' : (validation.warnings.length ? 'warning' : 'success');
  return <Badge variant="dot" color={color} overlap="circular"><Box sx={{ width:14, height:14 }} /></Badge>;
};

const ValidationSummary = ({ validation }) => (
  <Box>
    <Typography variant="subtitle2">Validation</Typography>
    {validation.errors.map((e,i) => <Typography key={i} color="error" variant="caption" display="block">• {e.message}</Typography>)}
    {validation.warnings.map((w,i) => <Typography key={i} color="warning.main" variant="caption" display="block">• {w.message}</Typography>)}
    {validation.errors.length === 0 && validation.warnings.length === 0 && <Typography variant="caption">No issues.</Typography>}
  </Box>
);

const ExperimentLevel = ({ dragType, isDraggingRef, onAddBlock }) => {
  const { state, addBlock, setMeta, navigate, removeBlock, addVariant, setAssignment } = useBuilder();
  const { draft } = state;
  const [title, setTitle] = React.useState(draft.meta.title);
  const [intro, setIntro] = React.useState(draft.meta.introduction || '');
  const [debrief, setDebrief] = React.useState(draft.meta.debrief || '');
  const [assignmentJson, setAssignmentJson] = React.useState(JSON.stringify(draft.assignment||{ strategy:'round_robin' }, null, 2));
  const [assignmentErr, setAssignmentErr] = React.useState(null);
  React.useEffect(()=>{
    try { const parsed = JSON.parse(assignmentJson); setAssignment(parsed); setAssignmentErr(null); } catch(e){ setAssignmentErr(e.message); }
  }, [assignmentJson, setAssignment]);
  const hasVariants = draft.variants.length>0;
  return (
    <Box>
      <Typography variant="h6" gutterBottom>Experiment Overview</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>Add blocks and then drill in to configure their contents.</Typography>
      <Stack spacing={1} sx={{ mb:2 }}>
        <TextField label="Title" size="small" value={title} onChange={e=>{const v=e.target.value; setTitle(v); setMeta({ title: v });}} />
        <TextField label="Introduction" multiline minRows={2} value={intro} onChange={e=>{const v=e.target.value; setIntro(v); setMeta({ introduction: v });}} />
        <TextField label="Debrief" multiline minRows={2} value={debrief} onChange={e=>{const v=e.target.value; setDebrief(v); setMeta({ debrief: v });}} />
      </Stack>
      <Box sx={{ mt:1 }}>
        <Typography variant="subtitle2" gutterBottom>Blocks (Drag to reorder)</Typography>
        <Droppable droppableId="builder-blocks" type={dragType}>
          {(provided)=>(
            <Stack spacing={1} ref={provided.innerRef} {...provided.droppableProps} sx={{ userSelect:'none' }}>
              {draft.blocks.map((b,i)=>(
                <Draggable draggableId={b.id} index={i} key={b.id}>
                  {(prov)=>(
                    <Paper ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps} sx={{ p:1, display:'flex', alignItems:'center', gap:1, cursor:'grab', border: state.ui.selectedBlockId===b.id ? '1px solid #1976d2' : '1px solid transparent', userSelect:'none' }}>
                      <Typography variant="body2" sx={{ flexGrow:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} onClick={()=> { if(!isDraggingRef.current) navigate({ level:'block', selectedBlockId:b.id }); }}>{i+1}. {b.title || '('+b.type+')'}</Typography>
                      <Tooltip title="Delete Block"><IconButton size="small" onClick={()=> removeBlock(b.id)}><DeleteIcon fontSize="inherit" /></IconButton></Tooltip>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
        <AddBlockButton onAdd={(type)=> { (onAddBlock||addBlock)(type); }} size="small" sx={{ mt:1 }} />
      </Box>
      <Box sx={{ mt:3 }}>
        <Paper variant="outlined" sx={{ p:1.5 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb:1 }}>
            <Typography variant="subtitle2" flexGrow={1}>Variants</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" disabled={draft.blocks.length===0} onClick={()=> addVariant()}>Clone Base as Variant</Button>
              <AdvancedVariantsDialogTrigger />
            </Stack>
          </Box>
          {draft.variants.length === 0 && <Typography variant="caption" color="text.secondary">No variants yet. Cloning base freezes further base edits.</Typography>}
          {draft.variants.length > 0 && (
            <Stack spacing={1}>
              {draft.variants.map(v => (
                <Paper key={v.id} sx={{ p:1, cursor:'pointer', border:'1px solid', borderColor: state.ui.selectedVariantId===v.id? 'primary.main':'divider' }} onClick={()=> navigate({ level:'variant', selectedVariantId: v.id, selectedBlockId:null, selectedTrialId:null })}>
                  <Typography variant="body2" sx={{ fontWeight:600 }}>{v.label}</Typography>
                  <Typography variant="caption" color="text.secondary">Blocks: {v.blocks.length}</Typography>
                </Paper>
              ))}
            </Stack>
          )}
          <Divider sx={{ my:1 }} />
          <Typography variant="subtitle2" sx={{ mb:0.5 }}>Assignment Strategy</Typography>
          <textarea style={{ width:'100%', minHeight:100, fontFamily:'monospace', fontSize:12 }} value={assignmentJson} onChange={e=> setAssignmentJson(e.target.value)} />
          {assignmentErr && <Typography variant="caption" color="error">{assignmentErr}</Typography>}
          {hasVariants && (
            <Box sx={{ mt:2 }}>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Variant Block Matrix (vs Base)</Typography>
              <VariantMatrix baseBlocks={draft.blocks} variants={draft.variants} />
              <Typography variant="caption" display="block" sx={{ mt:1 }}>
                Legend: <CheckCircleIcon sx={{ fontSize:12, verticalAlign:'middle', color:'success.main' }} /> unchanged | <EditIcon sx={{ fontSize:12, verticalAlign:'middle', color:'warning.main' }} /> modified | <RemoveCircleOutlineIcon sx={{ fontSize:12, verticalAlign:'middle', color:'error.main' }} /> removed | <AddCircleOutlineIcon sx={{ fontSize:12, verticalAlign:'middle', color:'info.main' }} /> added
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

// Variant read-only detail placeholder (Phase 1)
const VariantLevel = ({ variant }) => {
  const { navigate, addVariantBlock, removeVariantBlock, reorderVariantBlocks, state } = useBuilder();
  const vId = variant.id;
  const openBlock = (id) => navigate({ level:'block', selectedBlockId:id, selectedTrialId:null });
  const addBlock = (type) => addVariantBlock(vId, type);
  const move = (blockId, dir) => {
    const arr = [...variant.blocks];
    const idx = arr.findIndex(b=> b.id===blockId);
    if (idx === -1) return;
    const newIdx = dir === 'up' ? idx-1 : idx+1;
    if (newIdx < 0 || newIdx >= arr.length) return;
    const [item] = arr.splice(idx,1);
    arr.splice(newIdx,0,item);
    reorderVariantBlocks(variant.id, arr);
  };
  return (
    <Box>
      <Typography variant="h6" gutterBottom>{variant.label}</Typography>
      <Typography variant="body2" sx={{ mb:2 }}>Editing variant (base is frozen). Changes here affect overlay diffs on publish.</Typography>
      <Typography variant="subtitle2" sx={{ mb:1 }}>Blocks (Variant)</Typography>
      <Stack spacing={1}>
        {variant.blocks.map((b,i)=>(
          <Paper key={b.id} sx={{ p:1, border:'1px solid', borderColor: state.ui.selectedBlockId===b.id? 'primary.main':'divider' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ fontWeight:600, flexGrow:1, cursor:'pointer' }} onClick={()=> openBlock(b.id)}>{i+1}. {b.title || '('+b.type+')'}</Typography>
              <Tooltip title="Move Up"><span><IconButton size="small" disabled={i===0} onClick={()=> move(b.id,'up')}><ArrowUpwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
              <Tooltip title="Move Down"><span><IconButton size="small" disabled={i===variant.blocks.length-1} onClick={()=> move(b.id,'down')}><ArrowDownwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
              <Tooltip title="Delete Block"><span><IconButton size="small" onClick={()=> removeVariantBlock(variant.id, b.id)}><DeleteIcon fontSize="inherit" /></IconButton></span></Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">Type: {b.type} • Items: {(b.trials||b.questions||b.stimuli||[]).length}</Typography>
          </Paper>
        ))}
        {variant.blocks.length === 0 && <Typography variant="caption">No blocks yet.</Typography>}
      </Stack>
      <AddBlockButton onAdd={addBlock} size="small" sx={{ mt:2 }} />
    </Box>
  );
};

const VariantList = ({ variants, currentVariantId, onSelect }) => {
  return (
    <Box sx={{ mb:2 }}>
      <Typography variant="subtitle2" sx={{ mb:0.5 }}>Variants ({variants.length})</Typography>
      <Stack spacing={0.5}>
        {variants.map(v => (
          <Paper key={v.id} onClick={()=> onSelect(v.id)} sx={{ p:0.75, cursor:'pointer', border:'1px solid', borderColor: v.id===currentVariantId? 'primary.main':'divider', bgcolor: v.id===currentVariantId? 'action.selected':undefined }}>
            <Typography variant="body2" noWrap>{v.label}</Typography>
            <Typography variant="caption" color="text.secondary">Blocks: {v.blocks.length}</Typography>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

// Matrix visualization with deep diff: columns = variants, rows = block index (including added rows)
const VariantMatrix = ({ baseBlocks, variants }) => {
  const maxLen = Math.max(baseBlocks.length, ...variants.map(v=> v.blocks.length));
  const rows = Array.from({ length: maxLen }, (_,i) => ({ base: baseBlocks[i], index:i }));

  // Exclude keys that are noisy / runtime only
  const EXCLUDE_KEYS = new Set(['created_at','updated_at','hash','_id']);

  // Categorize path for human tooltip grouping
  const categorize = (path) => {
    if (/trials\[.*?\]\.questions/.test(path)) return 'trial questions';
    if (/trials\[/.test(path)) return 'trials';
    if (/questions\[/.test(path)) return 'questions';
    if (/stimuliSetId|stimuli\[/.test(path)) return 'stimuli';
    if (/scale(Id|Meta)/.test(path)) return 'scale';
    if (/orientation|leftPane|conversationMode|agentStimuliAccess/.test(path)) return 'primed settings';
    if (/randomization/.test(path)) return 'randomization';
    if (/introText|outroText/.test(path)) return 'text';
    if (/title/.test(path)) return 'title';
    return 'other';
  };

  const pushReason = (reasons, path, a, b) => {
    const cat = categorize(path);
    const leaf = path.split('.').slice(-1)[0];
    reasons.push(`${cat}: ${leaf} changed`);
  };

  const deepDiff = (a, b, reasons, path='') => {
    if (a === b) return; // identical primitive or same ref
    if (a == null || b == null) { pushReason(reasons, path || '(block)', a, b); return; }
    if (typeof a !== 'object' || typeof b !== 'object') { pushReason(reasons, path || '(block)', a, b); return; }
    // Arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) pushReason(reasons, path || '(array)', a.length, b.length);
      const len = Math.max(a.length, b.length);
      for (let i=0;i<len;i++) {
        if (i>=a.length || i>=b.length) { pushReason(reasons, `${path}[${i}]`, a[i], b[i]); continue; }
        deepDiff(a[i], b[i], reasons, `${path}[${i}]`);
      }
      return;
    }
    // Objects
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    keys.forEach(k => {
      if (EXCLUDE_KEYS.has(k)) return;
      const na = a[k];
      const nb = b[k];
      const childPath = path ? `${path}.${k}` : k;
      if (typeof na === 'object' && na && typeof nb === 'object' && nb) {
        deepDiff(na, nb, reasons, childPath);
      } else if (JSON.stringify(na) !== JSON.stringify(nb)) {
        pushReason(reasons, childPath, na, nb);
      }
    });
  };

  const cellStatus = (blockIndex, variant) => {
    const base = baseBlocks[blockIndex];
    const vb = variant.blocks[blockIndex];
    if (base && !vb) return { status:'removed', reasons:['Block removed'] };
    if (!base && vb) return { status:'added', reasons:['New block'] };
    if (!base && !vb) return { status:'empty', reasons:[] };
    const reasons = [];
    deepDiff(base, vb, reasons);
    // Deduplicate & limit
    const unique = Array.from(new Set(reasons));
    const LIMITED_COUNT = 15;
    const finalReasons = unique.slice(0, LIMITED_COUNT);
    if (unique.length > LIMITED_COUNT) finalReasons.push(`… +${unique.length-LIMITED_COUNT} more`);
    return { status: finalReasons.length ? 'modified' : 'unchanged', reasons: finalReasons };
  };

  const iconFor = (status) => {
    switch(status){
      case 'unchanged': return <CheckCircleIcon sx={{ fontSize:14, color:'success.main' }} />;
      case 'modified': return <EditIcon sx={{ fontSize:14, color:'warning.main' }} />;
      case 'removed': return <RemoveCircleOutlineIcon sx={{ fontSize:14, color:'error.main' }} />;
      case 'added': return <AddCircleOutlineIcon sx={{ fontSize:14, color:'info.main' }} />;
      default: return <Box sx={{ width:14, height:14 }} />;
    }
  };

  return (
    <Box sx={{ overflowX:'auto', border:'1px solid #ddd', borderRadius:1, background:'#fafafa' }}>
      <Box sx={{ display:'grid', gridTemplateColumns: `200px repeat(${variants.length}, 70px)` }}>
        <Box sx={{ p:0.5, fontSize:12, fontWeight:600, borderRight:'1px solid #ddd', bgcolor:'#f0f0f0' }}>Block</Box>
        {variants.map(v => (
          <Box key={v.id} sx={{ p:0.5, fontSize:11, textAlign:'center', borderLeft:'1px solid #ddd', bgcolor:'#f0f0f0', fontWeight:600 }}>{v.label}</Box>
        ))}
        {rows.map(row => {
          const { base, index } = row;
          const label = base ? `${index+1}. ${base.title || '('+base.type+')'}` : `(Added) ${variants.find(v=> v.blocks[index])?.blocks[index]?.title || 'Block '+(index+1)}`;
          return (
            <React.Fragment key={index}>
              <Box sx={{ p:0.5, fontSize:11, borderTop:'1px solid #eee', borderRight:'1px solid #ddd', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontStyle: base ? 'normal':'italic', opacity: base?1:0.85 }}>{label}</Box>
              {variants.map(v => {
                const { status, reasons } = cellStatus(index, v);
                return (
                  <Tooltip key={v.id} title={reasons.length ? reasons.join('\n') : ''} placement="top" arrow>
                    <Box sx={{ p:0.25, display:'flex', alignItems:'center', justifyContent:'center', borderTop:'1px solid #eee', borderLeft:'1px solid #ddd', bgcolor: status==='modified' ? 'rgba(255,193,7,0.18)' : status==='removed' ? 'rgba(244,67,54,0.18)' : status==='added' ? 'rgba(33,150,243,0.18)' : undefined }}>
                      {iconFor(status)}
                    </Box>
                  </Tooltip>
                );
              })}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

// Advanced JSON editing dialog for variants & assignment
const AdvancedVariantsDialogTrigger = () => {
  const { state, setVariants, setAssignment } = useBuilder();
  const [open, setOpen] = React.useState(false);
  const [raw, setRaw] = React.useState(() => JSON.stringify({ variants: state.draft.variants, assignment: state.draft.assignment }, null, 2));
  const [error, setError] = React.useState(null);
  const save = () => {
    try {
      const parsed = JSON.parse(raw);
      if (parsed.variants && Array.isArray(parsed.variants)) setVariants(parsed.variants);
      if (parsed.assignment && typeof parsed.assignment === 'object') setAssignment(parsed.assignment);
      setError(null); setOpen(false);
    } catch(e){ setError(e.message); }
  };
  React.useEffect(()=>{
    if (open) setRaw(JSON.stringify({ variants: state.draft.variants, assignment: state.draft.assignment }, null, 2));
  }, [open, state.draft.variants, state.draft.assignment]);
  return (
    <>
      <Button size="small" variant="outlined" onClick={()=> setOpen(true)}>Advanced JSON</Button>
      {open && (
        <Dialog open={open} onClose={()=> setOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Variants & Assignment JSON</DialogTitle>
          <DialogContent dividers>
            <Typography variant="caption" display="block" sx={{ mb:1 }}>Edit raw variant objects (full snapshots) and assignment strategy. Overlays will be auto-generated on publish from these working copies.</Typography>
            <TextField multiline minRows={14} value={raw} onChange={e=> setRaw(e.target.value)} fullWidth size="small" InputProps={{ style:{ fontFamily:'monospace', fontSize:12 } }} />
            {error && <Alert severity="error" sx={{ mt:1, p:0.5 }}>{error}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={()=> setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={save}>Apply</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const BlockLevel = ({ block, trials, onAddTrial, onOpenTrial, onUpdateBlock, onRemoveBlock, dragType, isDraggingRef, variantMode, variantId, openPromptEditor }) => {
  const { state, reorderBlocks, reorderVariantBlocks } = useBuilder();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  // Scale data fetch (lazy) if this is a scale block
  const [scales, setScales] = React.useState([]);
  const [loadingScales, setLoadingScales] = React.useState(false);
  const [scaleError, setScaleError] = React.useState(null);
  const [scaleUploadBusy, setScaleUploadBusy] = React.useState(false);
  const [scaleUploadError, setScaleUploadError] = React.useState(null);
  const scaleUploadInputRef = React.useRef(null);
  const apiBase = React.useMemo(()=>{
    if (process.env.REACT_APP_API_BASE) return process.env.REACT_APP_API_BASE.replace(/\/$/,'');
    if (typeof window !== 'undefined' && window.location.port === '3000') return 'http://localhost:4000';
    return '';
  }, []);
  const fetchScales = React.useCallback(()=>{
    if (block.type !== 'scale') return;
    setLoadingScales(true); setScaleError(null);
    const ctrl = new AbortController();
    const url = apiBase + '/api/simulations/operators';
    console.log('[ScaleFetch] GET', url);
    (async () => {
      try {
        const headers = {};
        if (isAuthenticated) {
          try { headers.Authorization = 'Bearer ' + await getAccessTokenSilently(); } catch(tokenErr){ console.warn('[ScaleFetch] token error', tokenErr); }
        }
        const r = await fetch(url, { signal: ctrl.signal, headers });
        if (!r.ok) throw new Error('Failed to load scales: '+r.status);
        const data = await r.json();
        setScales(Array.isArray(data)?data:[]);
      } catch(err) {
        if (err.name !== 'AbortError') { console.warn('[ScaleFetch] error', err); setScaleError(err.message); }
      } finally { setLoadingScales(false); }
    })();
    return () => ctrl.abort();
  }, [apiBase, block.type, getAccessTokenSilently, isAuthenticated]);
  // Guarded one-time fetch to avoid aborting in-flight requests on state toggles
  const scalesFetchStartedRef = React.useRef(false);
  React.useEffect(()=>{
    if (block.type === 'scale' && scales.length === 0 && !scalesFetchStartedRef.current) {
      scalesFetchStartedRef.current = true;
      const cleanup = fetchScales();
      return () => {
        scalesFetchStartedRef.current = false;
        if (typeof cleanup === 'function') cleanup();
      };
    }
    return undefined;
  }, [block.type, scales.length, fetchScales]);

  // Stimuli sets (future feature) similar pattern to scales
  const [stimuliSets, setStimuliSets] = React.useState([]);
  const [loadingStimuliSets, setLoadingStimuliSets] = React.useState(false);
  const [stimuliSetError, setStimuliSetError] = React.useState(null);
  // NOTE: Backend provides GET /api/stimuli/sets returning array of
  // { _id,name,description,createdAt, files:[ { fileId, order, title, description, settings, (merged file metadata: filename, originalName, mimetype, url, size, category) } ], ... }
  // We map each file entry to a stimulus; legacy 'items' array fallback retained.
  const fetchStimuliSets = React.useCallback(()=>{
    if (block.type !== 'stimuli' && block.type !== 'primed_conversation') return;
    setLoadingStimuliSets(true); setStimuliSetError(null);
    const ctrl = new AbortController();
    const url = apiBase + '/api/stimuli/sets'; // Assumed endpoint; confirm server implementation.
    console.log('[StimuliSetFetch] GET', url);
    (async () => {
      try {
        const headers = {};
        if (isAuthenticated) {
          try { headers.Authorization = 'Bearer ' + await getAccessTokenSilently(); } catch(tokenErr){ console.warn('[StimuliSetFetch] token error', tokenErr); }
        }
        const r = await fetch(url, { signal: ctrl.signal, headers });
        if (!r.ok) throw new Error('Failed to load stimuli sets: '+r.status);
        const data = await r.json();
        setStimuliSets(Array.isArray(data)? data : []);
      } catch(err) {
        if (err.name !== 'AbortError') { console.warn('[StimuliSetFetch] error', err); setStimuliSetError(err.message); }
      } finally { setLoadingStimuliSets(false); }
    })();
    return () => ctrl.abort();
  }, [apiBase, block.type, getAccessTokenSilently, isAuthenticated]);
  // Guarded fetch for stimuli sets to avoid abort on in-flight when state toggles
  const stimuliFetchStartedRef = React.useRef(false);
  React.useEffect(()=>{
    if ((block.type === 'stimuli' || block.type === 'primed_conversation') && stimuliSets.length === 0 && !stimuliFetchStartedRef.current) {
      stimuliFetchStartedRef.current = true;
      const cleanup = fetchStimuliSets();
      return () => {
        stimuliFetchStartedRef.current = false;
        if (typeof cleanup === 'function') cleanup();
      };
    }
    return undefined;
  }, [block.type, stimuliSets.length, fetchStimuliSets]);

  // Providers/models discovery for engine selects
  const [provData, setProvData] = React.useState({ providers: [] });
  const [provLoading, setProvLoading] = React.useState(false);
  const provLoadingRef = React.useRef(false);
  const refreshProviders = React.useCallback(() => {
    if (provLoadingRef.current) return undefined; // prevent concurrent/reflexive reloads
    provLoadingRef.current = true;
    setProvLoading(true);
    const ctrl = new AbortController();
    const url = apiBase + '/api/providers';
    (async () => {
      try {
        console.log('[Providers] fetching block-level providers from', url);
        const headers = {};
        if (isAuthenticated) {
          try { headers.Authorization = 'Bearer ' + await getAccessTokenSilently(); } catch {}
        }
        const r = await fetch(url, { signal: ctrl.signal, headers });
        if (!r.ok) throw new Error('Failed to load providers: ' + r.status);
        const data = await r.json();
        setProvData(data || { providers: [] });
        try {
          const summary = (data?.providers||[]).map(p=> `${p.id}:${Array.isArray(p.models)?p.models.length:0}${p.configured?'':' (not configured)'}`).join(', ');
          console.log('[Providers] block-level received', (data?.providers||[]).length, 'providers ->', summary);
        } catch {}
      } catch (e) {
        if (e.name !== 'AbortError') console.warn('[Providers] load failed', e);
      } finally { provLoadingRef.current = false; setProvLoading(false); }
    })();
    return () => ctrl.abort();
  }, [apiBase, getAccessTokenSilently, isAuthenticated]);
  // One-time providers fetch on mount when needed to avoid flicker/abort loops
  const provFetchStartedRef = React.useRef(false);
  React.useEffect(()=>{
    if (block.type !== 'primed_conversation') return;
    if (provFetchStartedRef.current) return;
    provFetchStartedRef.current = true;
    // Call without returning cleanup; button handles manual aborts if needed
    refreshProviders();
    return undefined;
  }, [block.type, refreshProviders]);

  // Removed unused agents hook in block-level (selection happens elsewhere)
  // Block-level agent selection for primed_conversation single_for_block mode
  const usingBlockConversationAgents = block.type === 'primed_conversation' && block.conversationMode === 'single_for_block';
  const { agents: blockAgents, loading: blockAgentsLoading, error: blockAgentsError, refresh: refreshBlockAgents } = useAgents({
    apiBase,
    enabled: usingBlockConversationAgents
  });

  // Ensure a single conversation trial object exists for single_for_block mode so we have a place to store agents/engine/limit
  React.useEffect(()=>{
    if (!usingBlockConversationAgents) return;
    const conv = block.conversation || {};
    let trialsArr = Array.isArray(conv.trials) ? conv.trials : [];
    if (trialsArr.length === 0) {
      trialsArr = [{
        id: 'conv-' + Math.random().toString(36).slice(2,9),
        agents: [],
        engine: { provider:'', model:'', temperature:0.7, top_p:1 },
        limit: { type:'messages', value:10 },
        task_description: '',
        expected_output: ''
      }];
      onUpdateBlock({ conversation: { ...conv, trials: trialsArr } });
    } else {
      // Ensure mandatory sub-fields exist (non-destructive augment)
      const first = trialsArr[0];
      const patchNeeded = !first.engine || !first.limit;
      if (patchNeeded) {
        const updatedFirst = {
          ...first,
          engine: first.engine || { provider:'', model:'', temperature:0.7, top_p:1 },
          limit: first.limit || { type:'messages', value:10 }
        };
        const nextTrials = [updatedFirst, ...trialsArr.slice(1)];
        onUpdateBlock({ conversation: { ...conv, trials: nextTrials } });
      }
    }
  }, [usingBlockConversationAgents, block.conversation, onUpdateBlock]);

  const chooseStimuliSet = (setObj) => {
    // Server provides sets with a `files` array (each merged with file metadata).
    // We transform each file into an internal stimulus item.
    const rawFiles = Array.isArray(setObj.files) ? [...setObj.files] : [];
    // Sort by provided order if present
    rawFiles.sort((a,b)=> (a.order||0) - (b.order||0));
    const items = rawFiles.map((f, idx) => {
      const fileId = f.fileId || f.id || f._id;
      // Build absolute URL (server origin) to avoid relative mismatch when builder served from different port.
      const base = apiBase || '';
      const relativeUrl = f.url || (fileId ? `/api/stimuli/file/${fileId}` : null);
      const mediaUrl = relativeUrl ? (relativeUrl.startsWith('http') ? relativeUrl : base + relativeUrl) + `?v=${encodeURIComponent(f.updatedAt || f.modifiedAt || '')}` : null;
      const exposure = (typeof f.settings?.minViewTime === 'number') ? f.settings.minViewTime : 0; // map minViewTime to exposure ms
      return {
        id: fileId ? String(fileId) : `stim-${idx}`,
        label: f.title || f.label || f.originalName || f.filename || `Stimulus ${idx+1}`,
        media: mediaUrl,
        mediaMeta: {
          originalName: f.originalName,
          filename: f.filename,
          mimetype: f.mimetype,
          size: f.size,
          description: f.description || '',
          category: f.category
        },
        exposure,
        size: { width: 0, height: 0 },
        position: { x: 0, y: 0 }
      };
    });
    // Fallback: if no files but legacy `items` array exists
    if (items.length === 0 && Array.isArray(setObj.items)) {
      setObj.items.forEach((it, idx) => {
        items.push({
          id: it.id || it._id || `stim-${idx}`,
          label: it.label || it.name || `Stimulus ${idx+1}`,
          media: it.media || null,
          mediaMeta: it.mediaMeta || null,
          exposure: typeof it.exposure === 'number' ? it.exposure : 0,
          size: it.size || { width: 0, height: 0 },
          position: it.position || { x: 0, y: 0 }
        });
      });
    }
    onUpdateBlock({
      stimuliSetId: setObj.id || setObj._id || setObj._id?.$oid || null,
      stimuliSetMeta: {
        name: setObj.name,
        description: setObj.description,
        itemsCount: items.length,
        created_at: setObj.created_at || setObj.date_created || setObj.dateCreated
      },
      stimuli: items
    });
  };

  // Upload new stimuli set dialog state
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [uploadName, setUploadName] = React.useState('');
  const [uploadDesc, setUploadDesc] = React.useState('');
  const [uploadFiles, setUploadFiles] = React.useState([]);
  const [uploadBusy, setUploadBusy] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const handleFilesChange = (e) => {
    setUploadFiles(Array.from(e.target.files||[]));
  };
  const resetUploadForm = () => { setUploadName(''); setUploadDesc(''); setUploadFiles([]); setUploadError(null); };
  const openUpload = () => { resetUploadForm(); setUploadOpen(true); };
  const closeUpload = () => { if(!uploadBusy) setUploadOpen(false); };
  const submitUpload = async () => {
    if (!uploadName || uploadFiles.length === 0) { setUploadError('Name and at least one file are required.'); return; }
    setUploadBusy(true); setUploadError(null);
    try {
      // First upload all files via /api/stimuli/upload collecting fileIds
      const headers = {};
      if (isAuthenticated) {
        try { headers.Authorization = 'Bearer ' + await getAccessTokenSilently(); } catch {}
      }
      const fileIds = [];
      for (const file of uploadFiles) {
        const form = new FormData();
        form.append('files', file);
        // optional: experimentId could be passed later; omitted for now
        const uploadResp = await fetch(apiBase + '/api/stimuli/upload', { method:'POST', headers, body: form });
        if (!uploadResp.ok) {
          const status = uploadResp.status;
          let detail = '';
          try {
            const t = await uploadResp.text();
            detail = (t || '').slice(0, 400);
          } catch {}
          if (status === 413) {
            throw new Error('Upload rejected (413: Request Entity Too Large). This usually means the hosted reverse proxy is enforcing a low request size limit. Ask the server admin to increase the proxy upload limit (e.g., Nginx client_max_body_size) and retry.');
          }
          throw new Error(`File upload failed (${status})${detail ? `: ${detail}` : ''}`);
        }
        const uploadJson = await uploadResp.json();
        const uploaded = (uploadJson.uploaded || []);
        const ids = uploaded.map(u => ({ fileId: u.fileId?.toString?.() || u.fileId || (u._id?.toString?.()) }));
        if (ids.length === 0) throw new Error('No fileId returned from upload endpoint.');
        fileIds.push(...ids);
      }
      if (fileIds.length === 0) throw new Error('No files uploaded.');
      // Create set
      const createResp = await fetch(apiBase + '/api/stimuli/sets', {
        method:'POST',
        headers: { 'Content-Type':'application/json', ...headers },
        body: JSON.stringify({ name: uploadName, description: uploadDesc, files: fileIds })
      });
      if(!createResp.ok) throw new Error('Create set failed: '+createResp.status);
      const created = await createResp.json();
      // Refresh sets and select newly created
      const refreshed = await (async()=>{
        try {
          const r = await fetch(apiBase + '/api/stimuli/sets', { headers });
          if(!r.ok) throw new Error('Refresh sets failed');
          return await r.json();
        } catch(err) { console.warn('[StimuliSetRefresh] error', err); return []; }
      })();
      setStimuliSets(Array.isArray(refreshed)? refreshed: []);
      const newSet = refreshed.find(s=> (s._id && s._id.toString && s._id.toString() === created.stimuliSetId) || s._id === created.stimuliSetId || s.id === created.stimuliSetId);
      if (newSet) chooseStimuliSet(newSet);
      setUploadOpen(false);
    } catch(err) {
      setUploadError(err.message);
    } finally { setUploadBusy(false); }
  };

  // When a scale is chosen, populate questions (read-only snapshot of items)
  const chooseScale = (scale) => {
    const questions = Object.entries(scale.items || {}).map(([k,v]) => ({ id: k, text: v.text, reverse: !!v.reverse, options: (scale.response_model?.labels)||[] }));
    onUpdateBlock({
      scaleId: scale.id || scale._id?.$oid || scale._id,
      scaleMeta: {
        name: scale.name,
        version: scale.version,
        authors: scale.authors,
        description: scale.description,
        source: scale.source,
        response_model: scale.response_model,
        itemsCount: questions.length,
        created_at: scale.created_at || scale.date_created || scale.dateCreated
      },
      questions
    });
  };

  const uploadScaleFromFile = async (file) => {
    if (!file) return;
    setScaleUploadBusy(true);
    setScaleUploadError(null);
    try {
      const text = await file.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        throw new Error('Invalid JSON: ' + e.message);
      }

      // Accept Mongo export shapes and normalize minimally
      const scaleDoc = parsed && typeof parsed === 'object' ? { ...parsed } : null;
      if (!scaleDoc) throw new Error('Scale JSON must be an object.');
      // Drop any exported _id so server can generate a new one
      if (scaleDoc._id) delete scaleDoc._id;
      if (!scaleDoc.type) scaleDoc.type = 'scale';

      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) {
        try { headers.Authorization = 'Bearer ' + await getAccessTokenSilently(); } catch {}
      }
      const resp = await fetch(apiBase + '/api/simulations/operators', {
        method: 'POST',
        headers,
        body: JSON.stringify({ operator: scaleDoc })
      });
      if (!resp.ok) {
        let msg = `Upload failed (${resp.status})`;
        try {
          const t = await resp.text();
          if (t) msg += `: ${t.slice(0, 300)}`;
        } catch {}
        throw new Error(msg);
      }
      const json = await resp.json();
      // Refresh list and select uploaded scale if present
      fetchScales && fetchScales();
      if (json && json.operator) {
        chooseScale(json.operator);
      }
    } catch (e) {
      setScaleUploadError(e.message || String(e));
    } finally {
      setScaleUploadBusy(false);
      // Allow uploading same file again
      try { if (scaleUploadInputRef.current) scaleUploadInputRef.current.value = ''; } catch {}
    }
  };
  let idx, canUp, canDown;
  if (!variantMode) {
    idx = state.draft.blocks.findIndex(b => b.id === block.id);
    canUp = idx > 0; canDown = idx < state.draft.blocks.length -1;
  } else {
    const v = state.draft.variants.find(vv => vv.blocks.some(b=> b.id===block.id));
    const arr = v ? v.blocks : [];
    idx = arr.findIndex(b=> b.id===block.id);
    canUp = idx > 0; canDown = idx < arr.length -1;
  }
  const move = (dir) => {
    if (!variantMode) {
      const arr = [...state.draft.blocks];
      const newIdx = dir === 'up' ? idx -1 : idx +1;
      const [item] = arr.splice(idx,1);
      arr.splice(newIdx,0,item);
      reorderBlocks(arr);
    } else {
      const variant = state.draft.variants.find(vv => vv.id===variantId);
      if (!variant) return;
      const arr = [...variant.blocks];
      const newIdx = dir === 'up' ? idx -1 : idx +1;
      const [item] = arr.splice(idx,1);
      arr.splice(newIdx,0,item);
      reorderVariantBlocks && reorderVariantBlocks(variant.id, arr);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <TextField value={block.title} size="small" onChange={e=> onUpdateBlock({ title: e.target.value })} placeholder="Block title" fullWidth />
        <Tooltip title="Move Up"><span><IconButton size="small" disabled={!canUp} onClick={()=>move('up')}><ArrowUpwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
        <Tooltip title="Move Down"><span><IconButton size="small" disabled={!canDown} onClick={()=>move('down')}><ArrowDownwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
        <IconButton size="small" onClick={onRemoveBlock}><DeleteIcon fontSize="small" /></IconButton>
      </Box>
      <Typography variant="caption" gutterBottom>{block.type.toUpperCase()} BLOCK</Typography>
      <Divider sx={{ my: 1 }} />
      <Stack spacing={2} sx={{ mb:2 }}>
        <TextField label="Intro Text" placeholder="Shown at start of block" value={block.introText || ''} onChange={e=> onUpdateBlock({ introText: e.target.value })} size="small" multiline minRows={2} />
        <TextField label="Outro Text" placeholder="Shown at end of block" value={block.outroText || ''} onChange={e=> onUpdateBlock({ outroText: e.target.value })} size="small" multiline minRows={2} />
        <FormControl size="small">
          <InputLabel id={`rand-label-${block.id}`}>Randomization</InputLabel>
          <Select labelId={`rand-label-${block.id}`} label="Randomization" value={block.randomization || 'sequential'} onChange={e=> onUpdateBlock({ randomization: e.target.value })}>
            <MenuItem value="sequential">Sequential (as ordered)</MenuItem>
            <MenuItem value="shuffle">Shuffle Once</MenuItem>
            <MenuItem value="per-participant-shuffle">Shuffle Per Participant</MenuItem>
            <MenuItem value="reverse">Reverse Order</MenuItem>
          </Select>
        </FormControl>
        {/* Explanations toggle for conversation-type blocks */}
        {(block.type === 'conversation' || block.type === 'primed_conversation') && (
          <Box sx={{ border:'1px solid #555', borderRadius:1, p:2 }}>
            <FormControlLabel
              control={<Checkbox checked={!!(block.explanations?.enabled)} onChange={e => {
                const enabled = e.target.checked;
                onUpdateBlock({ explanations: { ...(block.explanations || {}), enabled, type: enabled ? (block.explanations?.type || 'eg-gsa') : (block.explanations?.type || '') } });
              }} />}
              label={<Typography variant="subtitle2">Enable Explanations</Typography>}
            />
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb:1, ml:4 }}>
              When enabled, each agent message will be automatically analysed and an explanation message will appear in the chat.
            </Typography>
            {block.explanations?.enabled && (
              <FormControl size="small" sx={{ ml:4, minWidth:200 }}>
                <InputLabel id={`expl-type-${block.id}`}>Explanation Type</InputLabel>
                <Select labelId={`expl-type-${block.id}`} label="Explanation Type" value={block.explanations?.type || 'eg-gsa'} onChange={e=> onUpdateBlock({ explanations: { ...(block.explanations||{}), type: e.target.value } })}>
                  <MenuItem value="eg-gsa">EG-GSA (Gender Stereotype Analysis)</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </Stack>
      {block.type !== 'scale' && (
        <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={onAddTrial}>Add {block.type === 'conversation' ? 'Agent Trial' : block.type === 'stimuli' ? 'Stimulus' : block.type === 'primed_conversation' ? 'Trial' : 'Question'}</Button>
      )}
      {block.type === 'scale' && (
        <Box mt={3}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ flexGrow: 1, mb: 0 }}>Select a Scale</Typography>
            <input
              ref={scaleUploadInputRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={(e) => uploadScaleFromFile((e.target.files || [])[0])}
            />
            <Button
              size="small"
              variant="outlined"
              disabled={scaleUploadBusy}
              onClick={() => { if (scaleUploadInputRef.current) scaleUploadInputRef.current.click(); }}
            >
              Upload Scale JSON
            </Button>
          </Stack>
          {scaleUploadError && <Alert severity="error" sx={{ mb: 1, p: 0.5 }}>{scaleUploadError}</Alert>}
          {loadingScales && <CircularProgress size={24} />}
          {scaleError && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:1 }}>
              <Alert severity="error" sx={{ p:0.5, flexGrow:1 }}>Failed: {scaleError}</Alert>
              <Button size="small" onClick={fetchScales}>Retry</Button>
            </Stack>
          )}
          {!loadingScales && !scaleError && (
            <Box sx={{ display:'flex', overflowX:'auto', gap:2, pb:1 }}>
              {scales.map(s => {
                const selected = block.scaleId && (block.scaleId === s.id || block.scaleId === s._id || block.scaleId === s._id?.$oid);
                const formatDate = (d) => { if(!d) return ''; try { return new Date(d).toLocaleDateString(); } catch { return ''; } };
                const createdAt = s.created_at || s.date_created || s.dateCreated;
                return (
                  <Card key={s.id || s._id?.$oid || s._id} sx={{ minWidth:260, border: selected? '2px solid #1976d2':'1px solid #333', flex:'0 0 auto' }}>
                    <CardContent sx={{ pb:0 }}>
                      <Typography variant="subtitle2" noWrap>{s.name}</Typography>
                      <Typography variant="caption" display="block" sx={{ mb:0.5 }}>v{s.version} • {Array.isArray(s.authors)? s.authors.join(', '):''}{createdAt? ' • '+formatDate(createdAt):''}</Typography>
                      <Typography
                        variant="caption"
                        title={s.description}
                        sx={{
                          display:'-webkit-box',
                          WebkitLineClamp:2,
                          WebkitBoxOrient:'vertical',
                          overflow:'hidden',
                          textOverflow:'ellipsis',
                          whiteSpace:'normal',
                          wordBreak:'break-word',
                          maxWidth: 400,
                          lineHeight: 1.2,
                          minHeight: '2.4em'
                        }}
                      >{s.description}</Typography>
                    </CardContent>
                    <CardActions sx={{ pt:0 }}>
                      <Button size="small" onClick={()=> chooseScale(s)}>{selected? 'Selected':'Select'}</Button>
                    </CardActions>
                  </Card>
                );
              })}
              {scales.length === 0 && <Typography variant="caption">No scales available.</Typography>}
            </Box>
          )}
          {block.scaleId && block.scaleMeta && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Scale Details</Typography>
              <Typography variant="body2" sx={{ mb:1 }}>{block.scaleMeta.name} (v{block.scaleMeta.version})</Typography>
              <Typography variant="caption" display="block" sx={{ mb:1 }}>Items: {block.scaleMeta.itemsCount} • Response: {block.scaleMeta.response_model?.type}</Typography>
              <Typography variant="caption" display="block" sx={{ mb:1 }}>Authors: {(block.scaleMeta.authors||[]).join(', ')}</Typography>
              {block.scaleMeta.created_at && (
                <Typography variant="caption" display="block" sx={{ mb:1 }}>Date: {(() => { try { return new Date(block.scaleMeta.created_at).toLocaleDateString(); } catch { return block.scaleMeta.created_at; } })()}</Typography>
              )}
              {block.scaleMeta.source && <Typography variant="caption" display="block" sx={{ mb:1 }}>Source: <a href={block.scaleMeta.source} target="_blank" rel="noreferrer">link</a></Typography>}
              <Typography variant="subtitle2" sx={{ mt:2 }}>Items Preview</Typography>
              <Box sx={{ maxHeight:180, overflow:'auto', border:'1px solid #444', p:1 }}>
                {(block.questions||[]).map(q => (
                  <Typography key={q.id} variant="caption" display="block" sx={{ mb:0.5 }}>{q.id}. {q.text.slice(0,160)}{q.text.length>160?'…':''}</Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
      {block.type === 'primed_conversation' && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>Primed Conversation Settings</Typography>
          <Stack spacing={2} sx={{ mb:3 }}>
            <Stack direction={{ xs:'column', sm:'row' }} spacing={2}>
              <FormControl size="small" sx={{ minWidth:160 }}>
                <InputLabel id={`pc-layout-orient-${block.id}`}>Orientation</InputLabel>
                <Select labelId={`pc-layout-orient-${block.id}`} label="Orientation" value={block.layout?.orientation||'horizontal'} onChange={e=> onUpdateBlock({ layout: { ...(block.layout||{}), orientation: e.target.value } })}>
                  <MenuItem value="horizontal">Horizontal Split</MenuItem>
                  <MenuItem value="vertical">Vertical Split</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth:160 }}>
                <InputLabel id={`pc-left-pane-${block.id}`}>Left Pane</InputLabel>
                <Select labelId={`pc-left-pane-${block.id}`} label="Left Pane" value={block.layout?.leftPane||'stimuli'} onChange={e=>{
                  const left = e.target.value;
                  const right = left === 'stimuli' ? 'conversation' : 'stimuli';
                  onUpdateBlock({ layout: { ...(block.layout||{}), leftPane: left, rightPane: right } });
                }}>
                  <MenuItem value="stimuli">Stimuli</MenuItem>
                  <MenuItem value="conversation">Conversation</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth:200 }}>
                <InputLabel id={`pc-conv-mode-${block.id}`}>Conversation Mode</InputLabel>
                <Select labelId={`pc-conv-mode-${block.id}`} label="Conversation Mode" value={block.conversationMode||'single_for_block'} onChange={e=> onUpdateBlock({ conversationMode: e.target.value })}>
                  <MenuItem value="single_for_block">Single Conversation (shared)</MenuItem>
                  <MenuItem value="per_stimulus">Per Stimulus Conversation</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs:'column', sm:'row' }} spacing={2}>
              <FormControl size="small" sx={{ minWidth:220 }}>
                <InputLabel id={`pc-access-${block.id}`}>Agent Stimuli Access</InputLabel>
                <Select labelId={`pc-access-${block.id}`} label="Agent Stimuli Access" value={block.accessMode||'embedded'} onChange={e=> onUpdateBlock({ accessMode: e.target.value })}>
                  <MenuItem value="embedded">Fully Embedded (agent sees stimuli)</MenuItem>
                  <MenuItem value="interpreter">Interpreter (agent sees description)</MenuItem>
                  <MenuItem value="none">Exclusion (agent sees nothing)</MenuItem>
                </Select>
              </FormControl>
              {block.accessMode === 'interpreter' && (
                <TextField label="Interpreter Template" size="small" value={block.interpreter?.template||''} onChange={e=> onUpdateBlock({ interpreter: { ...(block.interpreter||{ strategy:'auto' }), template: e.target.value } })} fullWidth placeholder="e.g. A photograph showing {{stimulus.label}} (type: {{stimulus.mediaMeta.mimetype}})" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">Access Modes: Embedded shares full metadata; Interpreter substitutes a textual template; Exclusion shares nothing.</Typography>
          </Stack>
          {/* Shared conversation config if single_for_block */}
          {block.conversationMode === 'single_for_block' && (
            <Box sx={{ mb:3 }}>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Conversation Configuration</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:1 }}>
                {provLoading && <CircularProgress size={18} />}
                <Button size="small" onClick={refreshProviders} disabled={provLoading}>Refresh Providers</Button>
                {usingBlockConversationAgents && (
                  <Button size="small" onClick={refreshBlockAgents} disabled={blockAgentsLoading}>Refresh Agents</Button>
                )}
              </Stack>
              {(block.conversation?.trials||[]).slice(0,1).map(tr => (
                <Stack key={tr.id} spacing={2}>
                  {/* Agent selection carousel (mirrors TrialLevel but block-scoped) */}
                  {usingBlockConversationAgents && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb:1 }}>Agents</Typography>
                      {blockAgentsError && <Alert severity="error" sx={{ mb:1, p:0.5 }}>Agents load failed: {blockAgentsError}</Alert>}
                      <Box sx={{ display:'flex', overflowX:'auto', gap:2, pb:1 }}>
                        {blockAgentsLoading && <CircularProgress size={24} />}
                        {!blockAgentsLoading && blockAgents.map(a => {
                          const selected = (tr.agents||[]).includes(a.id);
                          const toggle = () => {
                            const current = tr.agents||[];
                            const nextAgents = selected ? current.filter(id=>id!==a.id) : [...current, a.id];
                            const trials = (block.conversation?.trials||[]).map(t=> t.id===tr.id ? { ...t, agents: nextAgents } : t);
                            onUpdateBlock({ conversation: { ...(block.conversation||{}), agents: nextAgents, trials } });
                          };
                          const avatarSrc = a.avatar ? (a.avatar.startsWith('data:') ? a.avatar : `data:image/png;base64,${a.avatar}`) : null;
                          return (
                            <Card key={a.id} sx={{ width:210, flex:'0 0 auto', border: selected? '2px solid #1976d2':'1px solid #333', position:'relative', display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
                              <CardContent sx={{ pb:0 }}>
                                <Box sx={{ display:'flex', alignItems:'center', mb:1, gap:1 }}>
                                  <Box component="img" src={avatarSrc || 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2248%22 height=%2248%22 viewBox=%220 0 48 48%22><rect width=%2248%22 height=%2248%22 rx=%228%22 fill=%22%23333%22/><text x=%2224%22 y=%2228%22 font-size=%2214%22 text-anchor=%22middle%22 fill=%22%23bbb%22 font-family=%22Arial, sans-serif%22>AI</text></svg>'} alt={a.name} sx={{ width:48, height:48, objectFit:'cover', borderRadius:1, border:'1px solid #444', flexShrink:0 }} />
                                  <Box sx={{ minWidth:0 }}>
                                    <Typography variant="subtitle2" noWrap title={a.name}>{a.name}</Typography>
                                    <Typography variant="caption" display="block" sx={{ color:'text.secondary' }}>{a.definitionMethod}</Typography>
                                  </Box>
                                </Box>
                                {a.crew_settings?.role && (
                                  <Typography variant="caption" sx={{ display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden', minHeight:32 }}>{a.crew_settings.role}</Typography>
                                )}
                              </CardContent>
                              <CardActions sx={{ pt:0, justifyContent:'flex-end' }}>
                                <Button size="small" onClick={toggle}>{selected? 'Remove':'Add'}</Button>
                              </CardActions>
                            </Card>
                          );
                        })}
                        {!blockAgentsLoading && blockAgents.length === 0 && <Typography variant="caption">No agents available.</Typography>}
                      </Box>
                      {(tr.agents||[]).length > 0 && (
                        <Paper variant="outlined" sx={{ p:1.25, mt:1 }}>
                          <Typography variant="subtitle2" sx={{ mb:1 }}>Selected Agents ({(tr.agents||[]).length})</Typography>
                          <Stack spacing={1}>
                            {(tr.agents||[]).map(id => {
                              const ag = blockAgents.find(a=>a.id===id);
                              if(!ag) return null;
                              const avatarSrc = ag.avatar ? (ag.avatar.startsWith('data:') ? ag.avatar : `data:image/png;base64,${ag.avatar}`) : null;
                              return (
                                <Box key={id} sx={{ border:'1px solid #333', borderRadius:1, p:1 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:0.5 }}>
                                    <Box component="img" src={avatarSrc || 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2232%22 height=%2232%22 viewBox=%220 0 48 48%22><rect width=%2248%22 height=%2248%22 rx=%228%22 fill=%22%23333%22/><text x=%2224%22 y=%2228%22 font-size=%2214%22 text-anchor=%22middle%22 fill=%22%23bbb%22 font-family=%22Arial, sans-serif%22>AI</text></svg>'} alt={ag.name} sx={{ width:32, height:32, objectFit:'cover', borderRadius:1, border:'1px solid #444' }} />
                                    <Box sx={{ minWidth:0, flexGrow:1 }}>
                                      <Typography variant="body2" noWrap title={ag.name}>{ag.name}</Typography>
                                      <Typography variant="caption" display="block" sx={{ color:'text.secondary' }}>{ag.definitionMethod}</Typography>
                                    </Box>
                                    <Button size="small" onClick={()=>{
                                      const nextAgents = (tr.agents||[]).filter(x=>x!==id);
                                      const trials = (block.conversation?.trials||[]).map(t=> t.id===tr.id ? { ...t, agents: nextAgents } : t);
                                      onUpdateBlock({ conversation: { ...(block.conversation||{}), agents: nextAgents, trials } });
                                    }}>Remove</Button>
                                  </Stack>
                                  {ag.crew_settings?.goal && (
                                    <Typography variant="caption" sx={{ color:'text.secondary', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{ag.crew_settings.goal}</Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        </Paper>
                      )}
                      <Typography variant="caption" display="block" sx={{ mt:0.5 }}>Select one or more agents to participate in this block-level conversation.</Typography>
                    </Box>
                  )}
                  <Stack direction={{ xs:'column', sm:'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth:180 }}>
                      <InputLabel id={`pc-prov-${tr.id}`}>Provider</InputLabel>
                      <Select labelId={`pc-prov-${tr.id}`} label="Provider" value={(tr.engine?.provider||'')} onChange={e=>{
                        const provider = e.target.value;
                        const trials = (block.conversation?.trials||[]).map(t=> t.id===tr.id ? { ...t, engine:{ ...(t.engine||{}), provider, model: '' } } : t);
                        onUpdateBlock({ conversation: { ...(block.conversation||{}), trials } });
                      }}>
                        {(provData.providers||[]).map(p=> (
                          <MenuItem key={p.id} value={p.id} disabled={!providerHasModels(p)}>{providerMenuLabel(p)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {(() => {
                      const provId = tr.engine?.provider || '';
                      const prov = (provData.providers||[]).find(p=> p.id===provId);
                      if (prov && Array.isArray(prov.models) && prov.models.length) {
                        return (
                          <FormControl size="small" sx={{ minWidth:200 }}>
                            <InputLabel id={`pc-model-${tr.id}`}>Model</InputLabel>
                            <Select labelId={`pc-model-${tr.id}`} label="Model" value={tr.engine?.model||''} onChange={e=>{
                              const nextModel = e.target.value;
                              const isGPT5 = (provId === 'openai') && typeof nextModel === 'string' && nextModel.toLowerCase().startsWith('gpt-5');
                              const trials = (block.conversation?.trials||[]).map(t=> {
                                if (t.id !== tr.id) return t;
                                const nextEngine = { ...(t.engine||{}), model: nextModel };
                                if (isGPT5) { nextEngine.temperature = undefined; nextEngine.top_p = undefined; }
                                return { ...t, engine: nextEngine };
                              });
                              onUpdateBlock({ conversation: { ...(block.conversation||{}), trials } });
                            }}>
                              {prov.models.map(m=> <MenuItem key={m.id} value={m.id}>{m.label||m.id}</MenuItem>)}
                            </Select>
                          </FormControl>
                        );
                      }
                      return (
                        <TextField label="Model" size="small" value={tr.engine?.model||''} disabled />
                      );
                    })()}
                    {(() => {
                      // Builder-time vision compatibility warning for single_for_block
                      const access = block.accessMode || 'embedded';
                      if (access !== 'embedded') return null;
                      const hasImage = Array.isArray(block.stimuli) && block.stimuli.some(s => (s.mediaMeta?.mimetype||'').startsWith('image/'));
                      if (!hasImage) return null;
                      const engine = tr.engine || {};
                      const provider = (engine.provider||'').toLowerCase();
                      const model = (engine.model||'').toLowerCase();
                      const supportsVision = (function(){
                        if (provider === 'openai') return model.startsWith('gpt-4o') || model.startsWith('o') || model.includes('vision') || model.startsWith('gpt-4.1');
                        if (provider === 'groq') return model.includes('vision') || model.endsWith('-v');
                        if (provider === 'anthropic' || provider === 'claude') return model.includes('sonnet') || model.includes('opus') || model.includes('haiku');
                        return false;
                      })();
                      const usesLocalhost = Array.isArray(block.stimuli) && block.stimuli.some(s => (s.media||'').startsWith('http://localhost'));
                      const msgs = [];
                      if (!supportsVision) msgs.push('Selected model may not support image inputs. Choose a vision-capable model.');
                      if (usesLocalhost) msgs.push('Image URL points to localhost; provider APIs cannot fetch local URLs. Use a publicly reachable URL or a data URI.');
                      if (msgs.length === 0) return null;
                      return <Alert severity="warning" sx={{ p:0.75 }}>{msgs.join(' ')}
                      </Alert>;
                    })()}
                    {(() => {
                      const p = tr.engine?.provider || '';
                      const m = (tr.engine?.model || '').toLowerCase();
                      const isGPT5 = p === 'openai' && m.startsWith('gpt-5');
                      if (isGPT5) {
                        return (
                          <Typography variant="caption" sx={{ color:'warning.main', alignSelf:'center' }}>
                            OpenAI GPT-5 models do not support temperature or top_p.
                          </Typography>
                        );
                      }
                      return (
                        <>
                          <TextField label="Temperature" size="small" type="number" inputProps={{ step:0.01, min:0, max:2 }} value={tr.engine?.temperature ?? 0.7} onChange={e=>{
                            const trials = (block.conversation?.trials||[]).map(t=> t.id===tr.id ? { ...t, engine:{ ...(t.engine||{}), temperature: parseFloat(e.target.value) } } : t);
                            onUpdateBlock({ conversation: { ...(block.conversation||{}), trials } });
                          }} />
                          <TextField label="Top P" size="small" type="number" inputProps={{ step:0.01, min:0, max:1 }} value={tr.engine?.top_p ?? 1} onChange={e=>{
                            const trials = (block.conversation?.trials||[]).map(t=> t.id===tr.id ? { ...t, engine:{ ...(t.engine||{}), top_p: parseFloat(e.target.value) } } : t);
                            onUpdateBlock({ conversation: { ...(block.conversation||{}), trials } });
                          }} />
                        </>
                      );
                    })()}
                  </Stack>
                  <Stack direction={{ xs:'column', sm:'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth:160 }}>
                      <InputLabel id={`pc-limit-type-${tr.id}`}>Limit Type</InputLabel>
                      <Select labelId={`pc-limit-type-${tr.id}`} label="Limit Type" value={tr.limit?.type || 'messages'} onChange={e=>{
                        const trials = (block.conversation?.trials||[]).map(t=> t.id===tr.id ? { ...t, limit:{ ...(t.limit||{}), type:e.target.value } } : t);
                        onUpdateBlock({ conversation: { ...(block.conversation||{}), trials } });
                      }}>
                        <MenuItem value="messages">Messages</MenuItem>
                        <MenuItem value="time">Time (minutes)</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField label={tr.limit?.type==='time' ? 'Minutes':'Messages'} size="small" type="number" inputProps={{ min:1 }} value={tr.limit?.value || 10} onChange={e=>{
                      const trials = (block.conversation?.trials||[]).map(t=> t.id===tr.id ? { ...t, limit:{ ...(t.limit||{}), value: parseInt(e.target.value||'0',10) } } : t);
                      onUpdateBlock({ conversation: { ...(block.conversation||{}), trials } });
                    }} />
                  </Stack>
                </Stack>
              ))}
              {/* Prompt Builder for single_for_block (edits first trial prompts) */}
              {(block.conversation?.trials||[]).slice(0,1).map(tr => (
                <Box key={tr.id} sx={{ mt:2 }}>
                  <Typography variant="subtitle2" sx={{ mb:1 }}>Task Prompt</Typography>
                  <Stack direction="row" spacing={1} sx={{ mb:1 }}>
                    <Button size="small" startIcon={<TuneIcon />} variant="outlined" onClick={()=> openPromptEditor && openPromptEditor({ scope:'primed_block', blockId:block.id, trialId: tr.id, ...(variantMode? { variantId } : {}) })}>Open Prompt Builder</Button>
                    {(tr.task_description||'').trim() && <Chip size="small" label="task_description set" />}
                    {(tr.expected_output||'').trim() && <Chip size="small" label="expected_output set" />}
                  </Stack>
                  <TextField label="Task Description (optional)" size="small" fullWidth multiline minRows={3} value={tr.task_description||''} onChange={e=>{
                    const trials = (block.conversation?.trials||[]).map(t=> t.id===tr.id ? { ...t, task_description: e.target.value } : t);
                    onUpdateBlock({ conversation: { ...(block.conversation||{}), trials } });
                  }} placeholder="Custom instructions; available tokens like {timer_info}, {history}, {user_message}, {stimulus.raw}, {stimulus.media_url}" />
                  <TextField sx={{ mt:1 }} label="Expected Output (optional)" size="small" fullWidth multiline minRows={2} value={tr.expected_output||''} onChange={e=>{
                    const trials = (block.conversation?.trials||[]).map(t=> t.id===tr.id ? { ...t, expected_output: e.target.value } : t);
                    onUpdateBlock({ conversation: { ...(block.conversation||{}), trials } });
                  }} placeholder="Describe the expected format; e.g., direct response and {agent_end}" />
                </Box>
              ))}
            </Box>
          )}
          {/* Inline stimuli set picker (reused from stimuli block UI) */}
          <Box sx={{ mt:2 }}>
            <Typography variant="subtitle2" gutterBottom>Select a Stimuli Set</Typography>
            <Stack direction="row" spacing={1} sx={{ mb:1 }}>
              <Button size="small" variant="outlined" onClick={openUpload}>Upload New Set</Button>
              <Button size="small" onClick={fetchStimuliSets} disabled={loadingStimuliSets}>Refresh</Button>
            </Stack>
            {loadingStimuliSets && <CircularProgress size={24} />}
            {stimuliSetError && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:1 }}>
                <Alert severity="error" sx={{ p:0.5, flexGrow:1 }}>Failed: {stimuliSetError}</Alert>
                <Button size="small" onClick={fetchStimuliSets}>Retry</Button>
              </Stack>
            )}
            {!loadingStimuliSets && !stimuliSetError && (
              <Box sx={{ display:'flex', overflowX:'auto', gap:2, pb:1 }}>
                {stimuliSets.map(s => {
                  const selected = block.stimuliSetId && (block.stimuliSetId === s.id || block.stimuliSetId === s._id || block.stimuliSetId === s._id?.$oid);
                  const formatDate = (d) => { if(!d) return ''; try { return new Date(d).toLocaleDateString(); } catch { return ''; } };
                  const createdAt = s.created_at || s.date_created || s.dateCreated;
                  return (
                    <Card key={s.id || s._id?.$oid || s._id} sx={{ minWidth:260, border: selected? '2px solid #1976d2':'1px solid #333', flex:'0 0 auto' }}>
                      <CardContent sx={{ pb:0 }}>
                        <Typography variant="subtitle2" noWrap>{s.name || s.title}</Typography>
                        <Typography variant="caption" display="block" sx={{ mb:0.5 }}>{Array.isArray(s.authors)? s.authors.join(', '):''}{createdAt? ' • '+formatDate(createdAt):''}</Typography>
                        <Typography
                          variant="caption"
                          title={s.description}
                          sx={{
                            display:'-webkit-box',
                            WebkitLineClamp:2,
                            WebkitBoxOrient:'vertical',
                            overflow:'hidden',
                            textOverflow:'ellipsis',
                            whiteSpace:'normal',
                            wordBreak:'break-word',
                            maxWidth: 400,
                            lineHeight: 1.2,
                            minHeight: '2.4em'
                          }}
                        >{s.description}</Typography>
                      </CardContent>
                      <CardActions sx={{ pt:0 }}>
                        <Button size="small" onClick={()=> chooseStimuliSet(s)}>{selected? 'Selected':'Select'}</Button>
                      </CardActions>
                    </Card>
                  );
                })}
                {stimuliSets.length === 0 && <Typography variant="caption">No stimuli sets available.</Typography>}
              </Box>
            )}
            {block.stimuliSetId && block.stimuliSetMeta && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>Stimuli Set Details</Typography>
                <Typography variant="body2" sx={{ mb:1 }}>{block.stimuliSetMeta.name}</Typography>
                <Typography variant="caption" display="block" sx={{ mb:1 }}>Items: {block.stimuliSetMeta.itemsCount}</Typography>
                {block.stimuliSetMeta.created_at && (
                  <Typography variant="caption" display="block" sx={{ mb:1 }}>Date: {(() => { try { return new Date(block.stimuliSetMeta.created_at).toLocaleDateString(); } catch { return block.stimuliSetMeta.created_at; } })()}</Typography>
                )}
                <Typography variant="subtitle2" sx={{ mt:2 }}>Items Preview</Typography>
                <Box sx={{ maxHeight:220, overflow:'auto', border:'1px solid #444', p:1, display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px,1fr))', gap:1 }}>
                  {(block.stimuli||[]).map(s => {
                    const type = s.mediaMeta?.mimetype || '';
                    const isImg = type.startsWith('image/');
                    return (
                      <Box key={s.id} sx={{ border:'1px solid #333', borderRadius:1, p:0.5, display:'flex', flexDirection:'column', alignItems:'center', gap:0.5 }}>
                        {isImg ? (
                          <Box component="img" src={s.media} alt={s.label} sx={{ width:'100%', height:60, objectFit:'cover', borderRadius:0.5 }} />
                        ) : (
                          <Box sx={{ width:'100%', height:60, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, bgcolor:'#222', borderRadius:0.5 }}>
                            {type.split('/')[1] || 'file'}
                          </Box>
                        )}
                        <Typography variant="caption" noWrap title={s.label}>{s.label}</Typography>
                        <Typography variant="caption" sx={{ fontSize:9, color:'text.secondary' }}>{(type||'').split('/').pop()}</Typography>
                      </Box>
                    );
                  })}
                  {(block.stimuli||[]).length===0 && <Typography variant="caption">No items yet.</Typography>}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {block.type === 'stimuli' && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>Select a Stimuli Set</Typography>
          <Stack direction="row" spacing={1} sx={{ mb:1 }}>
            <Button size="small" variant="outlined" onClick={openUpload}>Upload New Set</Button>
            <Button size="small" onClick={fetchStimuliSets} disabled={loadingStimuliSets}>Refresh</Button>
          </Stack>
          {loadingStimuliSets && <CircularProgress size={24} />}
          {stimuliSetError && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:1 }}>
              <Alert severity="error" sx={{ p:0.5, flexGrow:1 }}>Failed: {stimuliSetError}</Alert>
              <Button size="small" onClick={fetchStimuliSets}>Retry</Button>
            </Stack>
          )}
          {!loadingStimuliSets && !stimuliSetError && (
            <Box sx={{ display:'flex', overflowX:'auto', gap:2, pb:1 }}>
              {stimuliSets.map(s => {
                const selected = block.stimuliSetId && (block.stimuliSetId === s.id || block.stimuliSetId === s._id || block.stimuliSetId === s._id?.$oid);
                const formatDate = (d) => { if(!d) return ''; try { return new Date(d).toLocaleDateString(); } catch { return ''; } };
                const createdAt = s.created_at || s.date_created || s.dateCreated;
                return (
                  <Card key={s.id || s._id?.$oid || s._id} sx={{ minWidth:260, border: selected? '2px solid #1976d2':'1px solid #333', flex:'0 0 auto' }}>
                    <CardContent sx={{ pb:0 }}>
                      <Typography variant="subtitle2" noWrap>{s.name || s.title}</Typography>
                      <Typography variant="caption" display="block" sx={{ mb:0.5 }}>{Array.isArray(s.authors)? s.authors.join(', '):''}{createdAt? ' • '+formatDate(createdAt):''}</Typography>
                      <Typography
                        variant="caption"
                        title={s.description}
                        sx={{
                          display:'-webkit-box',
                          WebkitLineClamp:2,
                          WebkitBoxOrient:'vertical',
                          overflow:'hidden',
                          textOverflow:'ellipsis',
                          whiteSpace:'normal',
                          wordBreak:'break-word',
                          maxWidth: 400,
                          lineHeight: 1.2,
                          minHeight: '2.4em'
                        }}
                      >{s.description}</Typography>
                    </CardContent>
                    <CardActions sx={{ pt:0 }}>
                      <Button size="small" onClick={()=> chooseStimuliSet(s)}>{selected? 'Selected':'Select'}</Button>
                    </CardActions>
                  </Card>
                );
              })}
              {stimuliSets.length === 0 && <Typography variant="caption">No stimuli sets available.</Typography>}
            </Box>
          )}
          {block.stimuliSetId && block.stimuliSetMeta && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Stimuli Set Details</Typography>
              <Typography variant="body2" sx={{ mb:1 }}>{block.stimuliSetMeta.name}</Typography>
              <Typography variant="caption" display="block" sx={{ mb:1 }}>Items: {block.stimuliSetMeta.itemsCount}</Typography>
              {block.stimuliSetMeta.created_at && (
                <Typography variant="caption" display="block" sx={{ mb:1 }}>Date: {(() => { try { return new Date(block.stimuliSetMeta.created_at).toLocaleDateString(); } catch { return block.stimuliSetMeta.created_at; } })()}</Typography>
              )}
              <Typography variant="subtitle2" sx={{ mt:2 }}>Items Preview</Typography>
              <Box sx={{ maxHeight:220, overflow:'auto', border:'1px solid #444', p:1, display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px,1fr))', gap:1 }}>
                {(block.stimuli||[]).map(s => {
                  const type = s.mediaMeta?.mimetype || '';
                  const isImg = type.startsWith('image/');
                  return (
                    <Box key={s.id} sx={{ border:'1px solid #333', borderRadius:1, p:0.5, display:'flex', flexDirection:'column', alignItems:'center', gap:0.5 }}>
                      {isImg ? (
                        <Box component="img" src={s.media} alt={s.label} sx={{ width:'100%', height:60, objectFit:'cover', borderRadius:0.5 }} />
                      ) : (
                        <Box sx={{ width:'100%', height:60, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, bgcolor:'#222', borderRadius:0.5 }}>
                          {type.split('/')[1] || 'file'}
                        </Box>
                      )}
                      <Typography variant="caption" noWrap title={s.label}>{s.label}</Typography>
                      <Typography variant="caption" sx={{ fontSize:9, color:'text.secondary' }}>{(type||'').split('/').pop()}</Typography>
                    </Box>
                  );
                })}
                {(block.stimuli||[]).length===0 && <Typography variant="caption">No items yet.</Typography>}
              </Box>
            </Box>
          )}
        </Box>
      )}
      {uploadOpen && (
        <Dialog open={uploadOpen} onClose={closeUpload} maxWidth="sm" fullWidth>
          <DialogTitle>Upload New Stimuli Set</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              {uploadError && <Alert severity="error" sx={{ p:0.5 }}>{uploadError}</Alert>}
              <TextField label="Name" value={uploadName} size="small" onChange={e=> setUploadName(e.target.value)} fullWidth />
              <TextField label="Description" value={uploadDesc} size="small" onChange={e=> setUploadDesc(e.target.value)} fullWidth multiline minRows={2} />
              <Button variant="outlined" component="label" size="small">Select Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*,video/*,audio/*,application/pdf,text/plain,text/html,text/markdown,.md,.markdown"
                  onChange={handleFilesChange}
                />
              </Button>
              <Typography variant="caption">{uploadFiles.length} file(s) selected • Supports images, audio, video, PDF, text, and Markdown (.md)</Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeUpload} disabled={uploadBusy}>Cancel</Button>
            <Button onClick={submitUpload} disabled={uploadBusy} variant="contained">{uploadBusy? 'Uploading...':'Create Set'}</Button>
          </DialogActions>
        </Dialog>
      )}
      {block.type !== 'scale' && (
        <Box mt={2}>
          <Droppable droppableId={`block-items-${block.id}`} type={dragType}>
            {(provided)=>(
              <Stack ref={provided.innerRef} {...provided.droppableProps} spacing={1} sx={{ userSelect:'none' }}>
                {trials.map((t,i)=>(
                  <Draggable key={t.id} draggableId={t.id} index={i}>
                    {(prov)=>(
                      <Paper ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps} sx={{ p:1, cursor:'grab' }} onClick={()=> { if(!isDraggingRef.current) onOpenTrial(t.id); }}>
                        <Typography variant="body2" noWrap>{i+1}. {t.text || t.label || t.agentRef || '(untitled)'}</Typography>
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {trials.length === 0 && <Typography variant="caption">No items yet.</Typography>}
              </Stack>
            )}
          </Droppable>
        </Box>
      )}
    </Box>
  );
};

const TrialLevel = ({ block, trials, variantMode=false, variantId, openPromptEditor }) => {
  const { state, updateTrial, removeTrial, navigate, updateVariantTrial, removeVariantTrial } = useBuilder();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { ui } = state;
  const trial = (trials || []).find(t => t.id === ui.selectedTrialId);

  // Hooks must be declared before any early returns
  // Unified agent hook usage:
  const apiBase = React.useMemo(()=>{
    if (process.env.REACT_APP_API_BASE) return process.env.REACT_APP_API_BASE.replace(/\/$/,'');
    if (typeof window !== 'undefined' && window.location.port === '3000') return 'http://localhost:4000';
    return '';
  }, []);
  const usingConversationAgents = (block.type === 'conversation') || (block.type === 'primed_conversation' && block.conversationMode === 'per_stimulus');
  const { agents, loading: agentsLoading, error: agentsError, refresh: refreshAgents } = useAgents({
    apiBase,
    enabled: !!trial && usingConversationAgents
  });
  // Providers for trial-level engine selection
  const [provData, setProvData] = React.useState({ providers: [] });
  const [provLoading, setProvLoading] = React.useState(false);
  const provLoadingRef = React.useRef(false);
  const refreshProviders = React.useCallback(() => {
    if (provLoadingRef.current) return undefined;
    provLoadingRef.current = true;
    setProvLoading(true);
    const ctrl = new AbortController();
    const url = apiBase + '/api/providers';
    (async () => {
      try {
        console.log('[Providers] fetching trial-level providers from', url);
        const headers = {};
        if (isAuthenticated) { try { headers.Authorization = 'Bearer ' + await getAccessTokenSilently(); } catch {} }
        const r = await fetch(url, { signal: ctrl.signal, headers });
        if (!r.ok) throw new Error('Failed to load providers: ' + r.status);
        const data = await r.json();
        setProvData(data || { providers: [] });
        try {
          const summary = (data?.providers||[]).map(p=> `${p.id}:${Array.isArray(p.models)?p.models.length:0}${p.configured?'':' (not configured)'}`).join(', ');
          console.log('[Providers] trial-level received', (data?.providers||[]).length, 'providers ->', summary);
        } catch {}
      } catch (e) { if (e.name !== 'AbortError') console.warn('[Providers] trial-level load failed', e); }
      finally { provLoadingRef.current = false; setProvLoading(false); }
    })();
    return () => ctrl.abort();
  }, [apiBase, getAccessTokenSilently, isAuthenticated]);
  // One-time providers fetch for trial-level engine settings to avoid rapid re-fetch loops
  const trialProvFetchStartedRef = React.useRef(false);
  const trialId = trial?.id; // extracted to simplify deps for exhaustive-deps rule
  React.useEffect(()=>{
    if (!trialId || !usingConversationAgents) return;
    if (trialProvFetchStartedRef.current) return;
    trialProvFetchStartedRef.current = true;
    refreshProviders();
    return undefined;
  }, [trialId, usingConversationAgents, refreshProviders]);

  // Migration: ensure trial fields exist (create a merged object rather than mutating in place)
  React.useEffect(()=>{
    if (!trial) return;
    if (block.type !== 'conversation') return;
    const patch = {};
    if (!trial.engine) patch.engine = { provider:'', model:'', temperature:0.7, top_p:1 };
    if (!trial.agents) patch.agents = [];
    if (!trial.limit) patch.limit = { type: 'messages', value: 10 };
    if (Object.keys(patch).length) {
      updateTrial(block.id, trial.id, patch);
    }
  }, [block.id, block.type, trial, updateTrial]);

  // Migration for questionnaire likert trials: add likertConfig if missing
  React.useEffect(()=>{
    if (!trial) return;
    if (block.type !== 'questionnaire') return;
    if (trial.type !== 'likert') return;
    if (!trial.likertConfig) {
      const start = (trial.options && trial.options[0] === '0') ? 0 : 1;
      const levels = (trial.options && trial.options.length) ? trial.options.length : 7;
      const labels = new Array(levels).fill('');
      updateTrial(block.id, trial.id, { likertConfig: { start, levels, labels } });
    } else {
      // ensure consistency if options length and config levels diverge
      const cfg = trial.likertConfig;
      if (trial.type === 'likert' && (cfg.levels !== (trial.options?.length || 0))) {
        const labels = [...(cfg.labels||[])];
        if (labels.length < cfg.levels) {
          while (labels.length < cfg.levels) labels.push('');
        } else if (labels.length > cfg.levels) {
          labels.length = cfg.levels;
        }
        updateTrial(block.id, trial.id, { likertConfig: { ...cfg, labels } });
      }
    }
  }, [block.id, block.type, trial, updateTrial]);
  // Auto-detect natural image size for stimuli items (must be before any early return to satisfy hooks rules)
  React.useEffect(()=>{
    if (!trial) return; // safe guard
    if (block.type !== 'stimuli') return;
    if (!trial.media) return;
    const mimetype = trial.mediaMeta?.mimetype || '';
    if (!mimetype.startsWith('image/')) return; // only for images
    const w = trial.size?.width || 0; const h = trial.size?.height || 0;
    if (w > 0 && h > 0) return; // already set
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous'; // attempt to allow natural size read if CORS configured
    img.onload = () => {
      if (cancelled) return;
      const naturalW = img.naturalWidth || 0;
      const naturalH = img.naturalHeight || 0;
      if (naturalW && naturalH) {
        updateTrial(block.id, trial.id, { size: { width: naturalW, height: naturalH } });
      }
    };
    img.onerror = () => {
      // fallback: leave size unchanged; could log later
    };
    img.src = trial.media;
    return () => { cancelled = true; };
  }, [block.id, block.type, trial, updateTrial]);

  // Auto-detect natural image size for primed_conversation stimuli (same logic as stimuli block)
  React.useEffect(()=>{
    if (!trial) return;
    if (block.type !== 'primed_conversation') return;
    if (!trial.media) return;
    const mimetype = trial.mediaMeta?.mimetype || '';
    if (!mimetype.startsWith('image/')) return;
    const w = trial.size?.width || 0; const h = trial.size?.height || 0;
    if (w > 0 && h > 0) return; // already detected/overridden
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      const naturalW = img.naturalWidth || 0;
      const naturalH = img.naturalHeight || 0;
      if (naturalW && naturalH) {
        updateTrial(block.id, trial.id, { size: { width: naturalW, height: naturalH } });
      }
    };
    img.onerror = () => { /* silent */ };
    img.src = trial.media;
    return () => { cancelled = true; };
  }, [block.id, block.type, trial, updateTrial]);

  // Enhance preview mimetype inference when mediaMeta missing
  const inferredMime = React.useMemo(()=>{
    if (!trial) return '';
    if (trial.mediaMeta?.mimetype) return trial.mediaMeta.mimetype;
    if (!trial.media) return '';
    const m = trial.media.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    if (!m) return '';
    const ext = m[1].toLowerCase();
    const map = { jpg:'image/jpeg', jpeg:'image/jpeg', png:'image/png', gif:'image/gif', webp:'image/webp', svg:'image/svg+xml' };
    return map[ext] || '';
  }, [trial]);

  if (!trial) return <Typography variant="body2">Select a trial.</Typography>;
  const effectiveTrialAgents = Array.isArray(trial.agents)
    ? trial.agents
    : (Array.isArray(trial.conversation?.agents) ? trial.conversation.agents : []);
  const onChange = (patch) => {
    if (variantMode) updateVariantTrial(variantId, block.id, trial.id, patch); else updateTrial(block.id, trial.id, patch);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Editing Trial</Typography>
      {(block.type === 'conversation' || (block.type === 'primed_conversation' && block.conversationMode === 'per_stimulus')) && (
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb:1 }}>Agent Options</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:1 }}>
              {agentsLoading && <CircularProgress size={18} />}
              {!agentsLoading && <Button size="small" onClick={refreshAgents}>Refresh</Button>}
            </Stack>
            {agentsError && <Alert severity="error" sx={{ mb:1, p:0.5 }}>Agents load failed: {agentsError}</Alert>}
            {!agentsLoading && !agentsError && (
              <>
                <Box sx={{ display:'flex', overflowX:'auto', gap:2, pb:1 }}>
                  {agents.map(a => {
                    const selected = effectiveTrialAgents.includes(a.id);
                    const toggle = () => {
                      const current = effectiveTrialAgents;
                      onChange({ agents: selected ? current.filter(id=>id!==a.id) : [...current, a.id] });
                    };
                    const avatarSrc = a.avatar ? (a.avatar.startsWith('data:') ? a.avatar : `data:image/png;base64,${a.avatar}`) : null;
                    return (
                      <Card key={a.id} sx={{ width:220, flex:'0 0 auto', border: selected? '2px solid #1976d2':'1px solid #333', position:'relative', display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
                        <CardContent sx={{ pb:0 }}>
                          <Box sx={{ display:'flex', alignItems:'center', mb:1, gap:1 }}>
                            <Box component="img" src={avatarSrc || 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2248%22 height=%2248%22 viewBox=%220 0 48 48%22><rect width=%2248%22 height=%2248%22 rx=%228%22 fill=%22%23333%22/><text x=%2224%22 y=%2228%22 font-size=%2214%22 text-anchor=%22middle%22 fill=%22%23bbb%22 font-family=%22Arial, sans-serif%22>AI</text></svg>'} alt={a.name} sx={{ width:48, height:48, objectFit:'cover', borderRadius:1, border:'1px solid #444', flexShrink:0 }} />
                            <Box sx={{ minWidth:0 }}>
                              <Typography variant="subtitle2" noWrap title={a.name}>{a.name}</Typography>
                              <Typography variant="caption" display="block" sx={{ color:'text.secondary' }}>{a.definitionMethod}</Typography>
                            </Box>
                          </Box>
                          {a.crew_settings?.role && (
                            <Typography variant="caption" sx={{ display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden', minHeight:32 }}>{a.crew_settings.role}</Typography>
                          )}
                        </CardContent>
                        <CardActions sx={{ pt:0, justifyContent:'flex-end' }}>
                          <Button size="small" onClick={toggle}>{selected? 'Remove':'Add'}</Button>
                        </CardActions>
                      </Card>
                    );
                  })}
                  {agents.length === 0 && <Typography variant="caption">No agents available.</Typography>}
                </Box>
                {effectiveTrialAgents.length > 0 && (
                  <Paper variant="outlined" sx={{ p:1.5, mt:1 }}>
                    <Typography variant="subtitle2" sx={{ mb:1 }}>Selected Agents ({effectiveTrialAgents.length})</Typography>
                    <Stack spacing={1}>
                      {effectiveTrialAgents.map(id => {
                        const ag = agents.find(a=>a.id===id);
                        if(!ag) return null;
                        const avatarSrc = ag.avatar ? (ag.avatar.startsWith('data:') ? ag.avatar : `data:image/png;base64,${ag.avatar}`) : null;
                        return (
                          <Box key={id} sx={{ border:'1px solid #333', borderRadius:1, p:1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:0.5 }}>
                              <Box component="img" src={avatarSrc || 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2232%22 height=%2232%22 viewBox=%220 0 48 48%22><rect width=%2248%22 height=%2248%22 rx=%228%22 fill=%22%23333%22/><text x=%2224%22 y=%2228%22 font-size=%2214%22 text-anchor=%22middle%22 fill=%22%23bbb%22 font-family=%22Arial, sans-serif%22>AI</text></svg>'} alt={ag.name} sx={{ width:32, height:32, objectFit:'cover', borderRadius:1, border:'1px solid #444' }} />
                              <Box sx={{ minWidth:0, flexGrow:1 }}>
                                <Typography variant="body2" noWrap title={ag.name}>{ag.name}</Typography>
                                <Typography variant="caption" display="block" sx={{ color:'text.secondary' }}>{ag.definitionMethod}</Typography>
                              </Box>
                              <Button size="small" onClick={()=>{
                                const current = effectiveTrialAgents;
                                onChange({ agents: current.filter(x=>x!==id) });
                              }}>Remove</Button>
                            </Stack>
                            {ag.crew_settings?.goal && (
                              <Typography variant="caption" sx={{ color:'text.secondary', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{ag.crew_settings.goal}</Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>
                )}
              </>
            )}
            <Typography variant="caption" display="block" sx={{ mt:0.5 }}>Toggle agents to include them in this conversation.</Typography>
          </Box>
          
          {(block.type === 'conversation') && (
          <>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Engine Settings</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:1 }}>
                  {provLoading && <CircularProgress size={18} />}
                  {!agentsLoading && <Button size="small" onClick={refreshProviders} disabled={provLoading}>Refresh Providers</Button>}
                </Stack>
              <Stack direction={{ xs:'column', sm:'row' }} spacing={2} sx={{ mb:1 }}>
                <FormControl size="small" sx={{ minWidth:180 }}>
                  <InputLabel id={`conv-prov-${trial.id}`}>Provider</InputLabel>
                  <Select labelId={`conv-prov-${trial.id}`} label="Provider" value={trial.engine?.provider||''} onChange={e=>{
                    const provider = e.target.value;
                    onChange({ engine: { ...(trial.engine||{}), provider, model: '' } });
                  }}>
                    {(provData.providers||[]).map(p=> (
                      <MenuItem key={p.id} value={p.id} disabled={!providerHasModels(p)}>{providerMenuLabel(p)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(() => {
                  const provId = trial.engine?.provider || '';
                  const prov = (provData.providers||[]).find(p=> p.id===provId);
                  if (prov && Array.isArray(prov.models) && prov.models.length) {
                    return (
                      <FormControl size="small" sx={{ minWidth:200 }}>
                        <InputLabel id={`conv-model-${trial.id}`}>Model</InputLabel>
                        <Select labelId={`conv-model-${trial.id}`} label="Model" value={trial.engine?.model||''} onChange={e=>{
                          const nextModel = e.target.value;
                          const isGPT5 = (provId === 'openai') && typeof nextModel === 'string' && nextModel.toLowerCase().startsWith('gpt-5');
                          const nextEngine = { ...(trial.engine||{}), model: nextModel };
                          if (isGPT5) { nextEngine.temperature = undefined; nextEngine.top_p = undefined; }
                          onChange({ engine: nextEngine });
                        }}>
                          {prov.models.map(m=> <MenuItem key={m.id} value={m.id}>{m.label||m.id}</MenuItem>)}
                        </Select>
                      </FormControl>
                    );
                  }
                  return (
                    <TextField label="Model" size="small" value={trial.engine?.model||''} disabled fullWidth />
                  );
                })()}
              </Stack>
              {(() => {
                const p = trial.engine?.provider || '';
                const m = (trial.engine?.model || '').toLowerCase();
                const isGPT5 = p === 'openai' && m.startsWith('gpt-5');
                if (isGPT5) return <Typography variant="caption" sx={{ color:'warning.main' }}>OpenAI GPT-5 models do not support temperature or top_p.</Typography>;
                return (
                  <Stack direction={{ xs:'column', sm:'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth:160 }}>
                      <TextField label="Temperature" type="number" inputProps={{ step:0.01, min:0, max:2 }} size="small" value={trial.engine?.temperature ?? 0.7} onChange={e=> onChange({ engine: { ...trial.engine, temperature: parseFloat(e.target.value) } })} />
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth:160 }}>
                      <TextField label="Top P" type="number" inputProps={{ step:0.01, min:0, max:1 }} size="small" value={trial.engine?.top_p ?? 1} onChange={e=> onChange({ engine: { ...trial.engine, top_p: parseFloat(e.target.value) } })} />
                    </FormControl>
                  </Stack>
                );
              })()}
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Conversation Duration Limit</Typography>
              <Stack direction={{ xs:'column', sm:'row' }} spacing={2} alignItems={{ sm:'center' }}>
                <FormControl size="small" sx={{ minWidth:160 }}>
                  <InputLabel id="limit-type-label">Limit Type</InputLabel>
                <Select labelId="limit-type-label" label="Limit Type" value={trial.limit?.type || 'messages'} onChange={e=> onChange({ limit: { ...trial.limit, type: e.target.value } })}>
                  <MenuItem value="messages">Messages</MenuItem>
                  <MenuItem value="time">Time (minutes)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={trial.limit?.type === 'time' ? 'Minutes' : 'Messages'}
                size="small"
                type="number"
                inputProps={{ min:1, max: trial.limit?.type === 'time' ? 1440 : 500 }}
                value={trial.limit?.value || 10}
                onChange={e=> onChange({ limit: { ...trial.limit, value: parseInt(e.target.value || '0',10) } })}
              />
              </Stack>
              <Typography variant="caption" display="block" sx={{ mt:0.5 }}>Conversation ends after this many {trial.limit?.type==='time' ? 'minutes' : 'messages'}.</Typography>
            </Box>
            {/* Prompt editing for conversation trial */}
            <Divider sx={{ my:1.5 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Task Prompt</Typography>
              <Stack direction="row" spacing={1} sx={{ mb:1 }}>
                <Button size="small" startIcon={<TuneIcon />} variant="outlined" onClick={()=> openPromptEditor && openPromptEditor({ scope:'conversation_trial', blockId: block.id, trialId: trial.id, ...(variantMode? { variantId } : {}) })}>Open Prompt Builder</Button>
                {(trial.task_description||'').trim() && <Chip size="small" label="task_description set" />}
                {(trial.expected_output||'').trim() && <Chip size="small" label="expected_output set" />}
              </Stack>
              <TextField label="Task Description (optional)" size="small" fullWidth multiline minRows={3} value={trial.task_description||''} onChange={e=> onChange({ task_description: e.target.value })} placeholder="Tokens: {timer_info} {history} {user_message}" />
              <TextField sx={{ mt:1 }} label="Expected Output (optional)" size="small" fullWidth multiline minRows={2} value={trial.expected_output||''} onChange={e=> onChange({ expected_output: e.target.value })} placeholder="E.g., Direct response and {agent_end}" />
            </Box>
          </>
          )}
          {(block.type === 'primed_conversation' && block.conversationMode === 'per_stimulus') && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb:1 }}>Engine Settings</Typography>
                {/* Warning only; engine selection for per-stimulus is at block-level conversation.trials in current UI */}
                {(() => {
                  const access = block.accessMode || 'embedded';
                  if (access !== 'embedded') return null;
                  const mimetype = trial.mediaMeta?.mimetype || '';
                  const isImg = mimetype.startsWith('image/');
                  if (!isImg) return null;
                  const engine = (block.conversation?.trials||[])[0]?.engine || {};
                  const provider = (engine.provider||'').toLowerCase();
                  const model = (engine.model||'').toLowerCase();
                  const supportsVision = (function(){
                    if (provider === 'openai') return model.startsWith('gpt-4o') || model.startsWith('o') || model.includes('vision') || model.startsWith('gpt-4.1');
                    if (provider === 'groq') return model.includes('vision') || model.endsWith('-v');
                    if (provider === 'anthropic' || provider === 'claude') return model.includes('sonnet') || model.includes('opus') || model.includes('haiku');
                    return false;
                  })();
                  const usesLocalhost = (trial.media||'').startsWith('http://localhost');
                  const msgs = [];
                  if (!supportsVision) msgs.push('Selected model may not support image inputs. Choose a vision-capable model.');
                  if (usesLocalhost) msgs.push('Image URL points to localhost; provider APIs cannot fetch local URLs. Use a publicly reachable URL or a data URI.');
                  if (msgs.length === 0) return null;
                  return <Alert severity="warning" sx={{ p:0.75 }}>{msgs.join(' ')}</Alert>;
                })()}
              </Box>
            </>
          )}
          <Divider />
        </Stack>
      )}
      {(block.type === 'questionnaire') && (
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb:1 }}>Question Type</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={(()=>{
                  if (trial.type === 'multiple_choice') return 'single_choice'; // legacy rename
                  return trial.type || 'likert';
                })()}
                onChange={(e)=>{
                  let newType = e.target.value;
                  if (newType === 'single_choice') newType = 'single_choice';
                  const patch = { type: newType };
                  // Adjust options based on type
                  if (newType === 'likert') patch.options = ['1','2','3','4','5','6','7'];
                  else if (newType === 'single_choice') patch.options = ['Option 1','Option 2','Option 3'];
                  else if (newType === 'multiple_selection') { patch.options = ['Option 1','Option 2','Option 3']; patch.multipleSelection = { min:0, max:null, layout:'vertical' }; }
                  else if (newType === 'boolean') patch.options = ['Yes','No'];
                  else if (newType === 'slider') { patch.slider = { min:0, max:100, step:1, labels:[] }; patch.options = []; }
                  else if (newType === 'text') patch.options = [];
                  if (newType !== 'likert' && trial.likertConfig) patch.likertConfig = undefined; // drop likert config when switching away
                  if (newType !== 'slider' && trial.slider) patch.slider = undefined;
                  if (newType !== 'multiple_selection' && trial.multipleSelection) patch.multipleSelection = undefined;
                  // ensure required present
                  if (trial.required === undefined) patch.required = true;
                  onChange(patch);
                }}
              >
                <MenuItem value="likert">Likert Scale</MenuItem>
                <MenuItem value="text">Free Text</MenuItem>
                <MenuItem value="boolean">Yes / No</MenuItem>
                <MenuItem value="single_choice">Single Choice</MenuItem>
                <MenuItem value="multiple_selection">Multiple Selection</MenuItem>
                <MenuItem value="slider">Slider</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <TextInput label="Question Text" value={trial.text} onChange={v => onChange({ text: v })} multiline />
            <FormControlLabel
              sx={{ mt:1 }}
              control={<Checkbox size="small" checked={trial.required !== false} onChange={(e)=> onChange({ required: e.target.checked })} />}
              label={<Typography variant="caption">Required</Typography>}
            />
          </Box>
          {/* Likert specialized builder */}
          {(trial.type === 'likert') && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Likert Scale Configuration</Typography>
              <Stack direction={{ xs:'column', sm:'row' }} spacing={3} alignItems={{ sm:'center' }} sx={{ mb:2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={(trial.likertConfig?.start ?? ((trial.options?.[0] === '0')?0:1)) === 0}
                      onChange={(e)=>{
                        const start = e.target.checked ? 0 : 1;
                        const levels = trial.likertConfig?.levels || (trial.options?.length)||7;
                        const newOptions = Array.from({ length: levels }, (_,i)=> String(start + i));
                        const labels = [...(trial.likertConfig?.labels||[])];
                        while (labels.length < levels) labels.push('');
                        if (labels.length > levels) labels.length = levels;
                        onChange({ options: newOptions, likertConfig: { start, levels, labels } });
                      }}
                    />
                  }
                  label={<Typography variant="caption">Start at 0</Typography>}
                  sx={{ mr:2 }}
                />
                <TextField
                  label="Levels"
                  size="small"
                  type="number"
                  inputProps={{ min:2, max:15 }}
                  value={trial.likertConfig?.levels || (trial.options?.length)||7}
                  onChange={(e)=>{
                    const levels = Math.max(2, Math.min(15, parseInt(e.target.value||'0',10)));
                    const start = trial.likertConfig?.start ?? ((trial.options?.[0] === '0') ? 0 : 1);
                    const newOptions = Array.from({ length: levels }, (_,i)=> String(start + i));
                    const labelsPrev = trial.likertConfig?.labels || [];
                    const labels = labelsPrev.slice(0, levels);
                    while (labels.length < levels) labels.push('');
                    onChange({ options: newOptions, likertConfig: { start, levels, labels } });
                  }}
                />
              </Stack>
              {/* Single-row adaptive label inputs */}
              <Box sx={{
                display:'grid',
                width:'100%',
                gap:1,
                maxWidth: '100%',
                gridTemplateColumns: (trial.options||[]).length ? `repeat(${(trial.options||[]).length}, minmax(${Math.max(40, Math.min(110, 160/((trial.options||[]).length||1)))}px, 1fr))` : '1fr'
              }}>
                {(trial.options||[]).map((val, idx)=> {
                  const labelsArr = trial.likertConfig?.labels || [];
                  const labelVal = labelsArr[idx] || '';
                  return (
                    <Box key={idx} sx={{ display:'flex', flexDirection:'column', alignItems:'center', border:'1px solid #333', borderRadius:1, p:0.5, minWidth:0 }}>
                      <Typography variant="caption" sx={{ mb:0.5, lineHeight:1 }}>{val}</Typography>
                      <TextField
                        size="small"
                        placeholder="Label"
                        value={labelVal}
                        onChange={(e)=>{
                          const labels = [...(trial.likertConfig?.labels||[])];
                          while (labels.length < (trial.likertConfig?.levels||labels.length)) labels.push('');
                          labels[idx] = e.target.value;
                          onChange({ likertConfig: { ...(trial.likertConfig||{ start: (trial.options?.[0]==='0'?0:1), levels:(trial.options?.length||7) }), labels } });
                        }}
                        inputProps={{ style:{ textAlign:'center', padding:'4px', fontSize:11 } }}
                      />
                    </Box>
                  );
                })}
              </Box>
              </Box>
          )}
          {/* Slider configuration */}
          {(trial.type === 'slider') && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Slider Configuration</Typography>
              <Stack direction={{ xs:'column', sm:'row' }} spacing={2} sx={{ mb:2 }}>
                <TextField label="Min" size="small" type="number" value={trial.slider?.min ?? 0} onChange={e=>{
                  const min = parseFloat(e.target.value||'0');
                  const slider = { ...(trial.slider||{ max:100, step:1, labels:[] }), min };
                  if (slider.max < min) slider.max = min;
                  onChange({ slider });
                }} />
                <TextField label="Max" size="small" type="number" value={trial.slider?.max ?? 100} onChange={e=>{
                  const max = parseFloat(e.target.value||'0');
                  const slider = { ...(trial.slider||{ min:0, step:1, labels:[] }), max };
                  if (slider.min > max) slider.min = max;
                  onChange({ slider });
                }} />
                <TextField label="Step" size="small" type="number" inputProps={{ min:1, step:1 }} value={trial.slider?.step ?? 1} onChange={e=>{
                  let step = parseInt(e.target.value||'1',10); if(isNaN(step) || step < 1) step = 1;
                  const slider = { ...(trial.slider||{ min:0, max:100, labels:[] }), step };
                  onChange({ slider });
                }} />
              </Stack>
              <Typography variant="caption" display="block" sx={{ mb:1 }}>Value Labels (optional): specify value and label text.</Typography>
              <Stack spacing={1} sx={{ mb:1 }}>
                {(trial.slider?.labels||[]).map((lv, idx)=>(
                  <Box key={idx} sx={{ display:'flex', gap:1 }}>
                    <TextField size="small" label="Value" type="number" value={lv.value} onChange={e=>{
                      const labels = [...(trial.slider?.labels||[])];
                      labels[idx] = { ...labels[idx], value: parseFloat(e.target.value||'0') };
                      onChange({ slider: { ...(trial.slider||{}), labels } });
                    }} sx={{ width:100 }} />
                    <TextField size="small" label="Label" value={lv.label} onChange={e=>{
                      const labels = [...(trial.slider?.labels||[])];
                      labels[idx] = { ...labels[idx], label: e.target.value };
                      onChange({ slider: { ...(trial.slider||{}), labels } });
                    }} sx={{ flexGrow:1 }} />
                    <IconButton size="small" onClick={()=>{
                      const labels = (trial.slider?.labels||[]).filter((_,i)=> i!==idx);
                      onChange({ slider: { ...(trial.slider||{}), labels } });
                    }}><DeleteIcon fontSize="inherit" /></IconButton>
                  </Box>
                ))}
                <Button size="small" variant="outlined" onClick={()=>{
                  const baseVal = (trial.slider?.min||0);
                  const existing = (trial.slider?.labels||[]).map(l=>l.value);
                  let nextVal = baseVal;
                  while(existing.includes(nextVal)) nextVal += (trial.slider?.step||1);
                  const labels = [...(trial.slider?.labels||[]), { value: nextVal, label: '' }];
                  onChange({ slider: { ...(trial.slider||{ min:0, max:100, step:1 }), labels } });
                }}>+ Add Value Label</Button>
              </Stack>
            </Box>
          )}
          {/* Multiple selection configuration */}
          {(trial.type === 'multiple_selection') && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Multiple Selection Settings</Typography>
              <Stack direction={{ xs:'column', sm:'row' }} spacing={2} sx={{ mb:2 }}>
                <TextField label="Min Selections" size="small" type="number" inputProps={{ min:0 }} value={trial.multipleSelection?.min === undefined || trial.multipleSelection?.min === null ? '' : trial.multipleSelection.min} onChange={e=>{
                  const raw = e.target.value;
                  const min = raw === '' ? null : Math.max(0, parseInt(raw,10));
                  const ms = { ...(trial.multipleSelection||{ max:null, layout:'vertical' }), min };
                  if (ms.max !== null && min !== null && ms.max < min) ms.max = min; // keep consistent
                  onChange({ multipleSelection: ms });
                }} />
                <TextField label="Max Selections" size="small" type="number" inputProps={{ min:0 }} value={trial.multipleSelection?.max ?? ''} onChange={e=>{
                  const raw = e.target.value; const max = raw===''? null : parseInt(raw,10);
                  const ms = { ...(trial.multipleSelection||{ min:0, layout:'vertical' }), max };
                  if (max !== null && ms.min > max) ms.min = max;
                  onChange({ multipleSelection: ms });
                }} />
                <FormControl size="small" sx={{ minWidth:140 }}>
                  <InputLabel id={`ms-layout-${trial.id}`}>Layout</InputLabel>
                  <Select labelId={`ms-layout-${trial.id}`} label="Layout" value={trial.multipleSelection?.layout || 'vertical'} onChange={e=>{
                    const ms = { ...(trial.multipleSelection||{ min:0, max:null }), layout: e.target.value };
                    onChange({ multipleSelection: ms });
                  }}>
                    <MenuItem value="vertical">Vertical</MenuItem>
                    <MenuItem value="horizontal">Horizontal</MenuItem>
                    <MenuItem value="grid">Grid</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          )}
          {/* Generic options editor for single choice / boolean / multi-select option text */}
          {(['single_choice','boolean','multiple_selection'].includes(trial.type || '')) && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Options</Typography>
              <Stack spacing={1}>
                {(trial.options||[]).map((opt, idx)=>(
                  <Box key={idx} sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <TextField size="small" value={opt} onChange={e=>{
                      const arr = [...(trial.options||[])];
                      arr[idx] = e.target.value;
                      onChange({ options: arr });
                    }} sx={{ flexGrow:1 }} />
                    { (trial.type === 'single_choice' || trial.type === 'multiple_selection') && (
                      <IconButton size="small" onClick={()=>{
                        const arr = (trial.options||[]).filter((_,i)=> i!==idx);
                        onChange({ options: arr });
                      }}><DeleteIcon fontSize="inherit" /></IconButton>
                    )}
                  </Box>
                ))}
                {(trial.type === 'single_choice' || trial.type === 'multiple_selection') && (
                  <Button size="small" variant="outlined" onClick={()=>{
                    const count = (trial.options||[]).length;
                    const label = `Option ${count+1}`;
                    onChange({ options: [...(trial.options||[]), label] });
                  }}>+ Add Option</Button>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      )}
      {block.type === 'scale' && (
        <Typography variant="body2" sx={{ mb:2 }}>Scale items are immutable here. Return to block view to review selection.</Typography>
      )}
      {block.type === 'stimuli' && (
        <Stack spacing={2}>
          <TextInput label="Label" value={trial.label} onChange={v => onChange({ label: v })} />
          <Box>
            <Typography variant="caption" display="block" sx={{ mb:0.5 }}>Exposure (ms)</Typography>
            <TextField
              size="small"
              type="number"
              inputProps={{ min:0, max: 10*60*1000, step:50 }}
              value={trial.exposure ?? 0}
              onChange={e=>{
                let val = parseInt(e.target.value||'0',10); if(isNaN(val)||val<0) val = 0; if (val > 10*60*1000) val = 10*60*1000; onChange({ exposure: val });
              }}
              sx={{ width:200 }}
            />
            <Typography variant="caption" display="block" sx={{ mt:0.5 }}>Max 10 minutes (600000 ms). Step 50.</Typography>
          </Box>
          <Box>
            <Typography variant="caption" display="block" sx={{ mb:0.5 }}>Detected / Override Size (px)</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Width" type="number" value={trial.size?.width ?? 0} onChange={e=>{ const width = Math.max(0, parseInt(e.target.value||'0',10)); onChange({ size: { ...(trial.size||{ height:0 }), width } }); }} />
              <TextField size="small" label="Height" type="number" value={trial.size?.height ?? 0} onChange={e=>{ const height = Math.max(0, parseInt(e.target.value||'0',10)); onChange({ size: { ...(trial.size||{ width:0 }), height } }); }} />
              <Button size="small" variant="outlined" onClick={()=>{
                // force re-detect by clearing to zeros; effect below will re-populate
                onChange({ size: { width:0, height:0 } });
              }}>Redetect</Button>
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" display="block" sx={{ mb:0.5 }}>Position (x,y)</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="X" type="number" value={trial.position?.x ?? 0} onChange={e=>{ const x = parseInt(e.target.value||'0',10)||0; onChange({ position: { ...(trial.position||{ y:0 }), x } }); }} />
              <TextField size="small" label="Y" type="number" value={trial.position?.y ?? 0} onChange={e=>{ const y = parseInt(e.target.value||'0',10)||0; onChange({ position: { ...(trial.position||{ x:0 }), y } }); }} />
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" display="block" sx={{ mb:0.5 }}>Preview</Typography>
            {trial.media ? (
              (()=>{
                const mimetype = trial.mediaMeta?.mimetype || inferredMime || '';
                const isImg = mimetype.startsWith('image/');
                if (isImg) {
                  return <Box component="img" src={trial.media} alt={trial.label} sx={{ maxWidth:320, maxHeight:240, objectFit:'contain', display:'block', border:'1px solid #333', p:0.5, borderRadius:1 }} />;
                }
                return <Typography variant="caption" color="text.secondary">No image preview for type: {mimetype || 'unknown'}.</Typography>;
              })()
            ) : <Typography variant="caption" color="text.secondary">No media reference.</Typography>}
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2" sx={{ mb:1 }}>Attached Questions</Typography>
            <StimulusQuestionsEditor block={block} stimulus={trial} onChange={(stimPatch)=> onChange(stimPatch)} />
          </Box>
        </Stack>
      )}
      {block.type === 'primed_conversation' && (
        <Stack spacing={2}>
          <TextInput label="Label" value={trial.label} onChange={v => onChange({ label: v })} />
          <Box>
            <Typography variant="caption" display="block" sx={{ mb:0.5 }}>Exposure (ms)</Typography>
            <TextField size="small" type="number" inputProps={{ min:0, max:600000, step:50 }} value={trial.exposure ?? 0} onChange={e=>{
              let val = parseInt(e.target.value||'0',10); if(isNaN(val)||val<0) val=0; if(val>600000) val=600000; onChange({ exposure: val });
            }} sx={{ width:200 }} />
          </Box>
          <Box>
            <Typography variant="caption" display="block" sx={{ mb:0.5 }}>Detected / Override Size (px)</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Width" type="number" value={trial.size?.width ?? 0} onChange={e=>{ const width = Math.max(0, parseInt(e.target.value||'0',10)); onChange({ size: { ...(trial.size||{ height:0 }), width } }); }} />
              <TextField size="small" label="Height" type="number" value={trial.size?.height ?? 0} onChange={e=>{ const height = Math.max(0, parseInt(e.target.value||'0',10)); onChange({ size: { ...(trial.size||{ width:0 }), height } }); }} />
              <Button size="small" variant="outlined" onClick={()=> onChange({ size:{ width:0, height:0 } })}>Redetect</Button>
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" display="block" sx={{ mb:0.5 }}>Position (x,y)</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="X" type="number" value={trial.position?.x ?? 0} onChange={e=>{ const x = parseInt(e.target.value||'0',10)||0; onChange({ position: { ...(trial.position||{ y:0 }), x } }); }} />
              <TextField size="small" label="Y" type="number" value={trial.position?.y ?? 0} onChange={e=>{ const y = parseInt(e.target.value||'0',10)||0; onChange({ position: { ...(trial.position||{ x:0 }), y } }); }} />
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" display="block" sx={{ mb:0.5 }}>Preview</Typography>
            {trial.media ? (
              (()=>{ const mimetype = trial.mediaMeta?.mimetype || inferredMime || ''; const isImg = mimetype.startsWith('image/'); if(isImg) return <Box component="img" src={trial.media} alt={trial.label} sx={{ maxWidth:320, maxHeight:240, objectFit:'contain', border:'1px solid #333', p:0.5, borderRadius:1 }} />; return <Typography variant="caption" color="text.secondary">No image preview for type: {mimetype||'unknown'}.</Typography>; })()
            ) : <Typography variant="caption" color="text.secondary">No media reference.</Typography>}
          </Box>
          {block.conversationMode === 'per_stimulus' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb:1 }}>Conversation (This Stimulus)</Typography>
              <Stack direction={{ xs:'column', sm:'row' }} spacing={2} sx={{ mb:1 }}>
                <FormControl size="small" sx={{ minWidth:180 }}>
                  <InputLabel id={`stim-prov-${trial.id}`}>Provider</InputLabel>
                  <Select labelId={`stim-prov-${trial.id}`} label="Provider" value={trial.conversation?.engine?.provider||''} onChange={e=>{
                    const provider = e.target.value;
                    onChange({ conversation:{ ...(trial.conversation||{ engine:{}, agents:[], limit:{ type:'messages', value:10 } }), engine:{ ...(trial.conversation?.engine||{}), provider, model: '' } } });
                  }}>
                    {(provData.providers||[]).map(p=> (
                      <MenuItem key={p.id} value={p.id} disabled={!providerHasModels(p)}>{providerMenuLabel(p)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(() => {
                  const provId = trial.conversation?.engine?.provider || '';
                  const prov = (provData.providers||[]).find(p=> p.id===provId);
                  if (prov && Array.isArray(prov.models) && prov.models.length) {
                    return (
                      <FormControl size="small" sx={{ minWidth:200 }}>
                        <InputLabel id={`stim-model-${trial.id}`}>Model</InputLabel>
                        <Select labelId={`stim-model-${trial.id}`} label="Model" value={trial.conversation?.engine?.model||''} onChange={e=> {
                          const nextModel = e.target.value;
                          const isGPT5 = (provId === 'openai') && typeof nextModel === 'string' && nextModel.toLowerCase().startsWith('gpt-5');
                          const base = (trial.conversation||{ engine:{}, agents:[], limit:{ type:'messages', value:10 } });
                          const nextEngine = { ...(trial.conversation?.engine||{}), model: nextModel };
                          if (isGPT5) { nextEngine.temperature = undefined; nextEngine.top_p = undefined; }
                          onChange({ conversation:{ ...base, engine: nextEngine } });
                        }}>
                          {prov.models.map(m=> <MenuItem key={m.id} value={m.id}>{m.label||m.id}</MenuItem>)}
                        </Select>
                      </FormControl>
                    );
                  }
                  return (
                    <TextField label="Model" size="small" value={trial.conversation?.engine?.model||''} disabled />
                  );
                })()}
                {(() => {
                  const p = trial.conversation?.engine?.provider || '';
                  const m = (trial.conversation?.engine?.model || '').toLowerCase();
                  const isGPT5 = p === 'openai' && m.startsWith('gpt-5');
                  if (isGPT5) {
                    return <Typography variant="caption" sx={{ color:'warning.main', alignSelf:'center' }}>OpenAI GPT-5 models do not support temperature or top_p.</Typography>;
                  }
                  return (
                    <>
                      <TextField label="Temp" size="small" type="number" inputProps={{ step:0.01, min:0, max:2 }} value={trial.conversation?.engine?.temperature ?? 0.7} onChange={e=> onChange({ conversation:{ ...(trial.conversation||{ engine:{}, agents:[], limit:{ type:'messages', value:10 } }), engine:{ ...(trial.conversation?.engine||{}), temperature: parseFloat(e.target.value) } } })} />
                      <TextField label="Top P" size="small" type="number" inputProps={{ step:0.01, min:0, max:1 }} value={trial.conversation?.engine?.top_p ?? 1} onChange={e=> onChange({ conversation:{ ...(trial.conversation||{ engine:{}, agents:[], limit:{ type:'messages', value:10 } }), engine:{ ...(trial.conversation?.engine||{}), top_p: parseFloat(e.target.value) } } })} />
                    </>
                  );
                })()}
              </Stack>
              <Stack direction={{ xs:'column', sm:'row' }} spacing={2} sx={{ mb:1 }}>
                <FormControl size="small" sx={{ minWidth:140 }}>
                  <InputLabel id={`stim-limit-type-${trial.id}`}>Limit</InputLabel>
                  <Select labelId={`stim-limit-type-${trial.id}`} label="Limit" value={trial.conversation?.limit?.type || 'messages'} onChange={e=> onChange({ conversation:{ ...(trial.conversation||{ engine:{}, agents:[], limit:{ type:'messages', value:10 } }), limit:{ ...(trial.conversation?.limit||{}), type:e.target.value } } })}>
                    <MenuItem value="messages">Messages</MenuItem>
                    <MenuItem value="time">Time (minutes)</MenuItem>
                  </Select>
                </FormControl>
                <TextField label={trial.conversation?.limit?.type==='time'?'Minutes':'Messages'} size="small" type="number" inputProps={{ min:1 }} value={trial.conversation?.limit?.value || 10} onChange={e=> onChange({ conversation:{ ...(trial.conversation||{ engine:{}, agents:[], limit:{ type:'messages', value:10 } }), limit:{ ...(trial.conversation?.limit||{}), value: parseInt(e.target.value||'0',10) } } })} />
              </Stack>
              {/* Prompt builder for per-stimulus conversation */}
              <Box sx={{ mt:1 }}>
                <Typography variant="subtitle2" sx={{ mb:1 }}>Task Prompt</Typography>
                <Stack direction="row" spacing={1} sx={{ mb:1 }}>
                  <Button size="small" startIcon={<TuneIcon />} variant="outlined" onClick={()=> openPromptEditor && openPromptEditor({ scope:'primed_stimulus', blockId:block.id, trialId: trial.id, ...(variantMode? { variantId } : {}) })}>Open Prompt Builder</Button>
                  {(trial.conversation?.task_description||'').trim() && <Chip size="small" label="task_description set" />}
                  {(trial.conversation?.expected_output||'').trim() && <Chip size="small" label="expected_output set" />}
                </Stack>
                <TextField label="Task Description (optional)" size="small" fullWidth multiline minRows={3} value={trial.conversation?.task_description||''} onChange={e=> onChange({ conversation:{ ...(trial.conversation||{ engine:{}, agents:[], limit:{ type:'messages', value:10 } }), task_description: e.target.value } })} placeholder="Custom instructions; tokens: {timer_info}, {history}, {user_message}" />
                <TextField sx={{ mt:1 }} label="Expected Output (optional)" size="small" fullWidth multiline minRows={2} value={trial.conversation?.expected_output||''} onChange={e=> onChange({ conversation:{ ...(trial.conversation||{ engine:{}, agents:[], limit:{ type:'messages', value:10 } }), expected_output: e.target.value } })} placeholder="Describe expected format; include tokens like {agent_end}" />
              </Box>
            </Box>
          )}
          <Divider />
          <Box>
            <Typography variant="subtitle2" sx={{ mb:1 }}>Attached Questions</Typography>
            <StimulusQuestionsEditor stimulus={trial} onChange={(stimPatch)=> onChange(stimPatch)} block={block} />
          </Box>
        </Stack>
      )}
      {block.type !== 'scale' && (
        <>
          <Divider sx={{ my: 2 }} />
          <Button color="error" variant="outlined" size="small" onClick={() => { variantMode ? removeVariantTrial(variantId, block.id, trial.id) : removeTrial(block.id, trial.id); navigate({ level: 'block', selectedTrialId: null }); }}>Delete Trial</Button>
        </>
      )}
    </Box>
  );
};

const TextInput = ({ label, value, onChange, multiline }) => {
  return (
    <Box>
      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>{label}</Typography>
      <textarea style={{ width: '100%', minHeight: multiline ? 80 : 32, padding: 8, fontFamily: 'inherit', fontSize: 14 }} value={value || ''} onChange={e => onChange(e.target.value)} />
    </Box>
  );
};

// Preview Dialog
const PreviewDialog = ({ open, onClose, hash }) => {
  const { serialize, state } = useBuilder();
  if (!open) return null;
  const json = serialize();
  const data = state.draft;
  const totalItems = data.blocks.reduce((acc,b) => acc + (b.trials?.length||0) + (b.questions?.length||0) + (b.stimuli?.length||0),0);
  const copy = () => { navigator.clipboard.writeText(json); };
  const download = () => {
    const blob = new Blob([json], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `experiment-draft-${data.id}.json`; a.click();
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Preview Draft</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb:1 }}>Blocks: {data.blocks.length} | Items: {totalItems}</Typography>
        <Typography variant="caption" display="block" sx={{ mb:1 }}>Hash: {hash}</Typography>
        <Box component="pre" sx={{ maxHeight: 320, overflow:'auto', background:'#111', color:'#eee', p:1, fontSize:12 }}>{json}</Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<ContentCopyIcon />} onClick={copy}>Copy</Button>
        <Button startIcon={<DownloadIcon />} onClick={download}>Download</Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const PublishDialog = ({ open, onClose, hash, validation }) => {
  const { state } = useBuilder();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [publishing, setPublishing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [success, setSuccess] = React.useState(null); // store exptID on success
  const data = state.draft;
  const totalItems = data.blocks.reduce((acc,b) => acc + (b.trials?.length||0) + (b.questions?.length||0) + (b.stimuli?.length||0),0);

  const buildPayload = React.useCallback(()=>{
    const taskType = 'custom_experiment';
    const parameters = { title: data.meta.title, introduction: data.meta.introduction, debrief: data.meta.debrief, hash };
    const collectAgentIdsFromBlocks = (blocks = []) => {
      const ids = new Set();
      blocks.forEach(b => {
        if (!b) return;
        if (b.type === 'conversation') {
          (b.trials||[]).forEach(t => (t.agents||[]).forEach(aid => aid && ids.add(aid)));
        } else if (b.type === 'primed_conversation') {
          if (b.conversationMode === 'single_for_block') {
            (b.conversation?.trials||[]).forEach(ct => (ct.agents||[]).forEach(aid => aid && ids.add(aid)));
          } else {
            (b.stimuli||[]).forEach(s => {
              const refs = Array.isArray(s?.conversation?.agents) ? s.conversation.agents : (Array.isArray(s?.agents) ? s.agents : []);
              refs.forEach(aid => aid && ids.add(aid));
            });
          }
        }
      });
      return ids;
    };
    const agentIdSet = new Set();
    collectAgentIdsFromBlocks(data.blocks).forEach(id => agentIdSet.add(id));
    const agents = Array.from(agentIdSet).map(id => ({ id }));
    const blocks = data.blocks.map(b => ({ ...b }));
    const payload = { task: { type: taskType, parameters, blocks }, agents };
    if (Array.isArray(data.variants) && data.variants.length) {
      const baseBlocks = data.blocks;
      // Collect base agent ids
      const baseAgentSet = collectAgentIdsFromBlocks(baseBlocks);
      const overlayVariants = data.variants.map((v, idx) => {
        const overlayBlocks = [];
        const maxLen = Math.max(baseBlocks.length, v.blocks.length);
        for (let i=0;i<maxLen;i++) {
          const bb = baseBlocks[i];
            const vb = v.blocks[i];
          if (bb && !vb) {
            overlayBlocks.push({ index:i, remove:true });
            continue;
          }
          if (!bb && vb) {
            overlayBlocks.push({ index:i, replace:true, ...vb });
            continue;
          }
          if (bb && vb) {
            // Shallow diff fields + deep diff of items arrays
            const itemsKey = bb.trials ? 'trials' : bb.questions ? 'questions' : bb.stimuli ? 'stimuli' : null;
            const changedSimple = [
              'type',
              'title',
              'introText',
              'outroText',
              'randomization',
              'layout',
              'accessMode',
              'conversationMode',
              'interpreter',
              'conversation',
              'explanations'
            ].some(k => JSON.stringify(bb[k]) !== JSON.stringify(vb[k]));
            let changedItems = false;
            if (itemsKey) {
              changedItems = JSON.stringify(bb[itemsKey]) !== JSON.stringify(vb[itemsKey]);
            }
            if (changedSimple || changedItems) {
              // full replace for now to guarantee fidelity
              overlayBlocks.push({ index:i, replace:true, ...vb });
            }
          }
        }
        // Agent diffs → overlay agent entries
        const variantAgentIds = collectAgentIdsFromBlocks(v.blocks || []);
        (v.agents||[]).forEach(a => { if (a?.id) variantAgentIds.add(a.id); });
        const overlayAgents = [];
        // removals
        baseAgentSet.forEach(id => { if (!variantAgentIds.has(id)) overlayAgents.push({ id, disable:true }); });
        // additions
        variantAgentIds.forEach(id => { if (!baseAgentSet.has(id)) overlayAgents.push({ id }); });
        return {
          id: String.fromCharCode(65+idx),
          label: v.label,
          mode: 'overlay',
          task: overlayBlocks.length ? { blocks: overlayBlocks } : undefined,
          agents: overlayAgents.length ? overlayAgents : undefined
        };
      });
      payload.variants = overlayVariants;
    }
    if (data.assignment && typeof data.assignment === 'object') payload.assignment = data.assignment;
    return payload;
  }, [data, hash]);

  const handlePublish = async () => {
    setError(null); setSuccess(null);
    if (validation.errors.length) return; // guard
    try {
      setPublishing(true);
      let token = null;
      if (isAuthenticated) {
        try { token = await getAccessTokenSilently(); } catch(e){ /* non-fatal */ }
      }
      const payload = buildPayload();
      const res = await fetch(`${API_URL}/api/experiment/new`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Publish failed (${res.status}): ${text}`);
      }
      const json = await res.json();
      setSuccess(json.exptID || 'uploaded');
    } catch(e) {
      setError(e.message || String(e));
    } finally {
      setPublishing(false);
    }
  };

  if (!open) return null; // moved after hooks to satisfy rules-of-hooks

  return (
    <Dialog open={open} onClose={publishing ? ()=>{} : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{success ? 'Published' : 'Publish Draft'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb:1 }}>Blocks: {data.blocks.length} | Items: {totalItems}</Typography>
        <Typography variant="caption" display="block" sx={{ mb:2 }}>Hash: {hash}</Typography>
        {success && (
          <Alert severity="success" sx={{ mb:2 }}>Experiment created with ID: {success}</Alert>
        )}
        {!success && (
          <>
            {validation.errors.length === 0 ? (
              <Typography variant="body2" color="success.main">No validation errors detected.</Typography>
            ) : (
              <Typography variant="body2" color="error.main">Errors remain; publish disabled.</Typography>
            )}
            {validation.warnings.length>0 && <Typography variant="caption" display="block" sx={{ mt:1 }}>Warnings: {validation.warnings.length}</Typography>}
          </>
        )}
        {error && <Alert severity="error" sx={{ mt:2, whiteSpace:'pre-wrap' }}>{error}</Alert>}
        {publishing && <Box sx={{ mt:2, display:'flex', alignItems:'center', gap:1 }}><CircularProgress size={18} /><Typography variant="caption">Publishing...</Typography></Box>}
        {!success && (
          <Box sx={{ mt:2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Submitting will POST to /api/experiment/new with agents referenced in blocks. Server will enrich agents & validate scales.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={publishing}>Close</Button>
        {!success && (
          <Button variant="contained" startIcon={<PublishIcon />} disabled={publishing || validation.errors.length>0} onClick={handlePublish}>
            {publishing ? 'Publishing...' : 'Publish'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// (Monkey patch removed; using internal handlers now)

// Editor for per-stimulus questions (subset identical to questionnaire logic)
const StimulusQuestionsEditor = ({ stimulus, onChange, block }) => {
  const questions = stimulus.questions || [];
  const updateQuestion = (qid, patch) => {
    const updated = questions.map(q => q.id === qid ? { ...q, ...patch } : q);
    onChange({ questions: updated });
  };
  const removeQuestion = (qid) => {
    const updated = questions.filter(q => q.id !== qid);
    onChange({ questions: updated });
  };
  const addQuestion = () => {
    const id = crypto.randomUUID();
    const base = { id, type:'likert', text:'', options:['1','2','3','4','5','6','7'], required:true };
    onChange({ questions: [...questions, base] });
  };
  return (
    <Box>
      <Stack spacing={1} sx={{ mb:1 }}>
        {questions.map((q, idx)=> (
          <Paper key={q.id} variant="outlined" sx={{ p:1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:1 }}>
              <Typography variant="caption" sx={{ flexGrow:1 }}>Q{idx+1}</Typography>
              <FormControl size="small" sx={{ minWidth:140 }}>
                <Select value={(()=>{ if(q.type==='multiple_choice') return 'single_choice'; return q.type; })()} onChange={(e)=>{
                  let newType = e.target.value;
                  if (newType === 'single_choice') newType = 'single_choice';
                  const patch = { type: newType };
                  // Adjust options based on type
                  if (newType === 'likert') patch.options = ['1','2','3','4','5','6','7'];
                  else if (newType === 'single_choice') patch.options = ['Option 1','Option 2','Option 3'];
                  else if (newType === 'multiple_selection') { patch.options = ['Option 1','Option 2','Option 3']; patch.multipleSelection = { min:0, max:null, layout:'vertical' }; }
                  else if (newType === 'boolean') patch.options = ['Yes','No'];
                  else if (newType === 'slider') { patch.slider = { min:0, max:100, step:1, labels:[] }; patch.options = []; }
                  else if (newType === 'text') patch.options = [];
                  if (newType !== 'likert' && q.likertConfig) patch.likertConfig = undefined; // drop likert config when switching away
                  if (newType !== 'slider' && q.slider) patch.slider = undefined;
                  if (newType !== 'multiple_selection' && q.multipleSelection) patch.multipleSelection = undefined;
                  updateQuestion(q.id, patch);
                }} displayEmpty>
                  <MenuItem value="likert">Likert</MenuItem>
                  <MenuItem value="text">Free Text</MenuItem>
                  <MenuItem value="boolean">Yes / No</MenuItem>
                  <MenuItem value="single_choice">Single Choice</MenuItem>
                  <MenuItem value="multiple_selection">Multiple Selection</MenuItem>
                  <MenuItem value="slider">Slider</MenuItem>
                </Select>
              </FormControl>
              <IconButton size="small" onClick={()=> removeQuestion(q.id)}><DeleteIcon fontSize="inherit" /></IconButton>
            </Stack>
            <TextField size="small" label="Text" fullWidth multiline value={q.text} onChange={e=> updateQuestion(q.id, { text: e.target.value })} sx={{ mb:1 }} />
            <FormControlLabel control={<Checkbox size="small" checked={q.required !== false} onChange={e=> updateQuestion(q.id, { required: e.target.checked })} />} label={<Typography variant="caption">Required</Typography>} />
            {/* Type-specific configs */}
            {q.type === 'likert' && (
              <Box sx={{ mt:1 }}>
                <Stack direction="row" spacing={2} sx={{ mb:1 }}>
                  <FormControlLabel control={<Checkbox size="small" checked={(q.likertConfig?.start ?? (q.options?.[0] === '0')?0:1) === 0} onChange={(e)=>{
                    const start = e.target.checked?0:1; const levels = q.likertConfig?.levels || (q.options?.length)||7; const newOpts = Array.from({ length: levels }, (_,i)=> String(start+i));
                    const labels = [...(q.likertConfig?.labels||[])]; while(labels.length < levels) labels.push(''); if(labels.length>levels) labels.length = levels;
                    updateQuestion(q.id, { options:newOpts, likertConfig: { start, levels, labels } });
                  }} />} label={<Typography variant="caption">Start 0</Typography>} />
                  <TextField size="small" label="Levels" type="number" inputProps={{ min:2, max:15 }} value={q.likertConfig?.levels || (q.options?.length)||7} onChange={e=>{
                    const levels = Math.max(2, Math.min(15, parseInt(e.target.value||'0',10))); const start = q.likertConfig?.start ?? (q.options?.[0]==='0'?0:1); const newOpts = Array.from({ length: levels }, (_,i)=> String(start+i));
                    const labelsPrev = q.likertConfig?.labels || []; const labels = labelsPrev.slice(0,levels); while(labels.length<levels) labels.push('');
                    updateQuestion(q.id, { options:newOpts, likertConfig: { start, levels, labels } });
                  }} />
                </Stack>
                {/* Single-row adaptive label inputs */}
                <Box sx={{
                  display:'grid',
                  width:'100%',
                  gap:1,
                  maxWidth: '100%',
                  gridTemplateColumns: (q.options||[]).length ? `repeat(${(q.options||[]).length}, minmax(${Math.max(40, Math.min(110, 160/((q.options||[]).length||1)))}px, 1fr))` : '1fr'
                }}>
                  {(q.options||[]).map((val,i)=> {
                    const labelsArr = q.likertConfig?.labels || [];
                    const labelVal = labelsArr[i] || '';
                    return (
                      <Box key={i} sx={{ display:'flex', flexDirection:'column', alignItems:'center', border:'1px solid #333', borderRadius:1, p:0.5, minWidth:0 }}>
                        <Typography variant="caption" sx={{ mb:0.5, lineHeight:1 }}>{val}</Typography>
                        <TextField
                          size="small"
                          placeholder="Label"
                          value={labelVal}
                          onChange={(e)=>{
                            const labels = [...(q.likertConfig?.labels||[])];
                            while (labels.length < (q.likertConfig?.levels||labels.length)) labels.push('');
                            labels[i] = e.target.value;
                            updateQuestion(q.id, { likertConfig: { ...(q.likertConfig||{ start: (q.options?.[0]==='0'?0:1), levels:(q.options?.length||7) }), labels } });
                          }}
                          inputProps={{ style:{ textAlign:'center', padding:'4px', fontSize:11 } }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
            {q.type === 'slider' && (
              <Stack direction={{ xs:'column', sm:'row' }} spacing={1} sx={{ mb:1 }}>
                <TextField size="small" label="Min" type="number" value={q.slider?.min ?? 0} onChange={e=>{ const min = parseFloat(e.target.value||'0'); const slider = { ...(q.slider||{ max:100, step:1, labels:[] }), min }; if (slider.max < min) slider.max = min; updateQuestion(q.id, { slider }); }} />
                <TextField size="small" label="Max" type="number" value={q.slider?.max ?? 100} onChange={e=>{ const max = parseFloat(e.target.value||'0'); const slider = { ...(q.slider||{ min:0, step:1, labels:[] }), max }; if (slider.min > max) slider.min = max; updateQuestion(q.id, { slider }); }} />
                <TextField size="small" label="Step" type="number" inputProps={{ min:1, step:1 }} value={q.slider?.step ?? 1} onChange={e=>{
                  let step = parseInt(e.target.value||'1',10); if(isNaN(step) || step < 1) step = 1;
                  const slider = { ...(q.slider||{ min:0, max:100, labels:[] }), step };
                  updateQuestion(q.id, { slider });
                }} />
              </Stack>
            )}
            {q.type === 'multiple_selection' && (
              <Stack direction={{ xs:'column', sm:'row' }} spacing={1} sx={{ mb:1 }}>
                <TextField size="small" label="Min" type="number" inputProps={{ min:0 }} value={q.multipleSelection?.min === undefined || q.multipleSelection?.min === null ? '' : q.multipleSelection.min} onChange={e=>{
                  const raw = e.target.value;
                  const min = raw === '' ? null : Math.max(0, parseInt(raw,10));
                  const ms = { ...(q.multipleSelection||{ max:null, layout:'vertical' }), min };
                  if (ms.max !== null && min !== null && ms.max < min) ms.max = min; // keep consistent
                  updateQuestion(q.id, { multipleSelection: ms });
                }} />
                <TextField size="small" label="Max" type="number" inputProps={{ min:0 }} value={q.multipleSelection?.max ?? ''} onChange={e=>{
                  const raw = e.target.value; const max = raw===''? null : parseInt(raw,10);
                  const ms = { ...(q.multipleSelection||{ min:0, layout:'vertical' }), max };
                  if (max !== null && ms.min > max) ms.min = max;
                  updateQuestion(q.id, { multipleSelection: ms });
                }} />
                <FormControl size="small" sx={{ minWidth:140 }}>
                  <InputLabel id={`ms-layout-${q.id}`}>Layout</InputLabel>
                  <Select labelId={`ms-layout-${q.id}`} label="Layout" value={q.multipleSelection?.layout || 'vertical'} onChange={e=>{
                    const ms = { ...(q.multipleSelection||{ min:0, max:null }), layout: e.target.value };
                    updateQuestion(q.id, { multipleSelection: ms });
                  }}>
                    <MenuItem value="vertical">Vertical</MenuItem>
                    <MenuItem value="horizontal">Horizontal</MenuItem>
                    <MenuItem value="grid">Grid</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}
            {(['single_choice','boolean','multiple_selection'].includes(q.type||'')) && (
              <Box sx={{ mb:1 }}>
                <Typography variant="caption" display="block" sx={{ mb:0.5 }}>Options</Typography>
                <Stack spacing={0.75}>
                  {(q.options||[]).map((opt,i)=>(
                    <Box key={i} sx={{ display:'flex', gap:1 }}>
                      <TextField size="small" value={opt} onChange={e=>{ const arr=[...(q.options||[])]; arr[i]=e.target.value; updateQuestion(q.id, { options: arr }); }} fullWidth />
                      { (q.type==='single_choice' || q.type==='multiple_selection') && (
                        <IconButton size="small" onClick={()=>{ const arr=(q.options||[]).filter((_,x)=>x!==i); updateQuestion(q.id, { options: arr }); }}><DeleteIcon fontSize="inherit" /></IconButton>
                      )}
                    </Box>
                  ))}
                  {(q.type==='single_choice' || q.type==='multiple_selection') && (
                    <Button size="small" variant="outlined" onClick={()=>{
                      const label = `Option ${(q.options||[]).length+1}`;
                      updateQuestion(q.id, { options:[...(q.options||[]), label] });
                    }}>+ Add Option</Button>
                  )}
                </Stack>
              </Box>
            )}
          </Paper>
        ))}
        {questions.length === 0 && <Typography variant="caption">No questions attached.</Typography>}
      </Stack>
  <Button size="small" variant="contained" onClick={addQuestion}>Add Question</Button>
    </Box>
  );
};

export default ExperimentBuilder;

// Import Dialog Component
const ImportDialog = ({ open, onClose }) => {
  const { importDraft } = useBuilder();
  const [rawText, setRawText] = React.useState('');
  const [fileName, setFileName] = React.useState('');
  const [parseError, setParseError] = React.useState(null);
  const [parsed, setParsed] = React.useState(null);
  const [summary, setSummary] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [lenient, setLenient] = React.useState(false);
  const fileInputRef = React.useRef(null);

  React.useEffect(()=>{
    if (!open) {
      setRawText(''); setFileName(''); setParseError(null); setParsed(null); setSummary(null); setBusy(false);
    }
  }, [open]);

  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => { setRawText(reader.result || ''); };
    reader.onerror = () => { setParseError('Failed to read file'); };
    reader.readAsText(f);
  };

  const attemptParse = () => {
    setParseError(null); setParsed(null); setSummary(null); setLenient(false);

    const tryParse = (text) => {
      const obj = JSON.parse(text);
      const blocks = Array.isArray(obj.blocks) ? obj.blocks.length : 0;
      const items = Array.isArray(obj.blocks)? obj.blocks.reduce((acc,b)=> acc + (b.trials?.length||0) + (b.questions?.length||0) + (b.stimuli?.length||0),0) : 0;
      setParsed(obj);
      setSummary({ blocks, items, title: obj.meta?.title });
    };

    // Lenient sanitizer for common non-JSON inputs (keeps string contents intact)
    const sanitizeForJson = (text) => {
      let input = text || '';
      // Remove BOM
      if (input.charCodeAt(0) === 0xFEFF) input = input.slice(1);

      // Split into string and non-string segments to avoid altering quoted text
      const segments = [];
      let i = 0, inStr = false, strQuote = '"', esc = false, buf = '';
      while (i < input.length) {
        const ch = input[i];
        if (inStr) {
          buf += ch;
          if (esc) { esc = false; }
          else if (ch === '\\') { esc = true; }
          else if (ch === strQuote) { // end string
            segments.push({ type: 'string', value: buf });
            buf = '';
            inStr = false;
          }
          i++;
          continue;
        }
        // Not in string
        if (ch === '"' || ch === '\'') {
          // flush pending non-string
          if (buf) { segments.push({ type: 'other', value: buf }); buf = ''; }
          inStr = true; strQuote = ch; esc = false; buf = ch; i++; continue;
        }
        buf += ch; i++;
      }
      if (buf) segments.push({ type: inStr ? 'string' : 'other', value: buf });

      const transformOther = (t) => {
        // Strip JS-style comments
        t = t.replace(/\/\*[\s\S]*?\*\//g, '').replace(/(^|[^:])\/\/.*$/gm, '$1');
        // Replace special JS tokens with JSON-safe values
        t = t
          .replace(/:undefined/g, ':null')
          .replace(/: undefined/g, ': null')
          .replace(/\bundefined\b/g, 'null')
          .replace(/\bNaN\b/g, 'null')
          .replace(/\b-?Infinity\b/g, 'null');
        // Quote unquoted object keys: {foo:1} -> {"foo":1}
  t = t.replace(/([{,]\s*)([A-Za-z_$][A-Za-z0-9_$]*)\s*:/g, '$1"$2":');
        // Fill empty array slots with nulls: [ , -> [ null,  |  , , -> , null,  |  , ] -> , null]
        t = t.replace(/\[\s*,/g, '[ null,');
        // iteratively replace double commas to handle runs of empty entries
        for (let k = 0; k < 6; k++) {
          const next = t.replace(/,\s*,/g, ', null,');
          if (next === t) break;
          t = next;
        }
        t = t.replace(/,\s*\]/g, ', null]');
        // Remove trailing commas in objects/arrays
        t = t.replace(/,\s*([}\]])/g, '$1');
        return t;
      };

      const out = segments.map(seg => seg.type === 'other' ? transformOther(seg.value) : seg.value).join('');
      return out;
    };

    try {
      tryParse(rawText);
    } catch (strictErr) {
      try {
        const cleaned = sanitizeForJson(rawText);
        if (cleaned !== rawText) {
          tryParse(cleaned);
          setLenient(true);
          setRawText(cleaned); // show user the adjusted JSON
        } else {
          throw strictErr;
        }
      } catch (lenientErr) {
        // Provide a friendlier error with a small context snippet if available
        const msg = lenientErr?.message || strictErr?.message || 'Failed to parse JSON';
        setParseError(msg);
      }
    }
  };

  const doImport = () => {
    if (!parsed) return;
    setBusy(true);
    try {
      importDraft(parsed);
      onClose();
    } catch (err) {
      setParseError(err.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onClose={busy? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Experiment JSON</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" size="small" onClick={()=> fileInputRef.current?.click()}>Select File</Button>
            <Typography variant="caption" sx={{ flexGrow:1 }}>{fileName || 'No file selected'}</Typography>
            <input type="file" accept="application/json,.json" ref={fileInputRef} style={{ display:'none' }} onChange={handleFile} />
            <Button size="small" onClick={attemptParse} disabled={!rawText}>Parse</Button>
          </Stack>
          <TextField label="Raw JSON" multiline minRows={6} value={rawText} onChange={e=> setRawText(e.target.value)} size="small" fullWidth placeholder="Paste JSON or choose file" />
          {parseError && <Alert severity="error" sx={{ p:0.5 }}>{parseError}</Alert>}
          {summary && !parseError && (
            <Paper variant="outlined" sx={{ p:1 }}>
              <Typography variant="subtitle2" sx={{ mb:0.5 }}>Parsed Summary</Typography>
              <Typography variant="caption" display="block">Title: {summary.title || '(none)'}</Typography>
              <Typography variant="caption" display="block">Blocks: {summary.blocks}</Typography>
              <Typography variant="caption" display="block">Items: {summary.items}</Typography>
              {lenient && <Alert severity="warning" sx={{ mt:1, p:0.5 }}>
                Non-standard tokens (e.g. undefined) were converted to null for parsing. Consider fixing the source JSON export.
              </Alert>}
              <Typography variant="caption" display="block" sx={{ mt:1 }}>Click Import to load & validate. Current draft will be replaced.</Typography>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={doImport} disabled={!parsed || !!parseError || busy}>{busy? 'Importing...' : 'Import'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// Reusable block type selection button
const AddBlockButton = ({ onAdd, size='small', sx }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const choose = (t) => { onAdd(t); handleClose(); };
  return (
    <>
      <Button startIcon={<AddIcon />} size={size} variant="contained" onClick={handleClick} sx={sx}>Add Block</Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} anchorOrigin={{ vertical:'bottom', horizontal:'left' }}>
        <MenuItem onClick={()=> choose('conversation')}>
          <ListItemIcon><ChatIcon fontSize="small" /></ListItemIcon>
          Conversation
        </MenuItem>
        <MenuItem onClick={()=> choose('primed_conversation')}>
          <ListItemIcon><ChatIcon fontSize="small" /></ListItemIcon>
          Primed Conversation
        </MenuItem>
        <MenuItem onClick={()=> choose('questionnaire')}>
          <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
          Questionnaire
        </MenuItem>
        <MenuItem onClick={()=> choose('stimuli')}>
          <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
          Stimuli
        </MenuItem>
        <MenuItem onClick={()=> choose('scale')}>
          <ListItemIcon><QuizIcon fontSize="small" /></ListItemIcon>
          Scale
        </MenuItem>
      </Menu>
    </>
  );
};

// Prompt Builder Dialog (reusable)
function usePromptTokens() {
  // Variables available in task prompts. Hover to see details.
  return React.useMemo(() => ([
    {
      key: '{timer_info}',
      label: 'Timer info',
      description: 'Runtime-generated notice about time/message limits for the current conversation.'
    },
    {
      key: '{history}',
      label: 'Conversation history',
      description: 'Full prior chat history (system, user, and agent messages) relevant to the current turn.'
    },
    {
      key: '{user_message}',
      label: 'User message',
      description: 'The latest user input that the agent should respond to now.'
    },
    {
      key: '{agent_end}',
      label: 'Agent end marker',
      description: 'A marker used by the runtime to detect the end of the agent’s response. Include where appropriate.'
    },
    // Primed conversation stimulus variables
    {
      key: '{stimulus.raw}',
      label: 'Stimulus (raw)',
      description: 'Raw stimulus content for text is provided as plain text. For images, this will be the image URL as well; prefer {stimulus.media_url} when available. Empty outside primed contexts.'
    },
    {
      key: '{stimulus.interpreted}',
      label: 'Stimulus (interpreted)',
      description: 'Interpreter-produced textual summary of the stimulus using the experiment’s interpreter template. Empty if not configured.'
    },
    {
      key: '{stimulus.media_url}',
      label: 'Stimulus media URL',
      description: 'Direct URL to the stimulus media (e.g., an image). In embedded mode with images, the agent is multimodal-enabled and can attach this image.'
    }
  ]), []);
}

const TokenPill = ({ token, onInsert }) => (
  <Tooltip title={token.description || token.label || token.key} arrow>
    <Chip
      label={token.key}
      size="small"
      onClick={()=> onInsert(token.key)}
      draggable
      onDragStart={(e)=>{ try { e.dataTransfer.setData('text/token', token.key); e.dataTransfer.setData('text/plain', token.key); } catch {} }}
      sx={{ cursor:'grab' }}
    />
  </Tooltip>
);

const PromptBuilderDialog = ({ open, onClose, tokens, initialDescription, initialOutput, onSave }) => {
  const [active, setActive] = React.useState('description');
  const [desc, setDesc] = React.useState(initialDescription||'');
  const [out, setOut] = React.useState(initialOutput||'');
  const descRef = React.useRef(null);
  const outRef = React.useRef(null);
  React.useEffect(()=>{ if (open){ setDesc(initialDescription||''); setOut(initialOutput||''); setActive('description'); } }, [open, initialDescription, initialOutput]);
  const insert = (token) => {
    const ref = active === 'description' ? descRef : outRef;
    const val = active === 'description' ? desc : out;
    const el = ref.current;
    let next = val + token;
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart; const end = el.selectionEnd;
      next = val.slice(0,start) + token + val.slice(end);
      setTimeout(()=>{ try { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; } catch {} }, 0);
    }
    if (active === 'description') setDesc(next); else setOut(next);
  };
  const doSave = () => { onSave && onSave(desc, out); };
  if (!open) return null;
  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Prompt Builder</DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs:'column', md:'row' }} spacing={2}>
          <Box sx={{ minWidth:{ md:220 }, borderRight:{ md:'1px solid #333' }, pr:{ md:1 } }}>
            <Typography variant="subtitle2" sx={{ mb:1 }}>Variables</Typography>
            <Stack spacing={0.5}>
              {tokens.map(t => (
                <TokenPill key={t.key} token={t} onInsert={insert} />
              ))}
            </Stack>
            <Typography variant="caption" display="block" sx={{ mt:1, color:'text.secondary' }}>Click a variable to insert at cursor.</Typography>
          </Box>
          <Box sx={{ flexGrow:1 }}>
            <Typography variant="subtitle2">Task Description</Typography>
            <TextField inputRef={descRef} multiline minRows={4} value={desc} onFocus={()=> setActive('description')} onChange={e=> setDesc(e.target.value)} fullWidth size="small" placeholder={"Custom instructions; e.g.\nYou are having a time-limited conversation. {timer_info}\nUse the conversation history:\n{history}\nRespond to: {user_message}"}
              onDragOver={(e)=> e.preventDefault()}
              onDrop={(e)=>{ e.preventDefault(); const t = e.dataTransfer.getData('text/token') || e.dataTransfer.getData('text/plain'); if (t) insert(t); }}
            />
            <Typography variant="subtitle2" sx={{ mt:2 }}>Expected Output</Typography>
            <TextField inputRef={outRef} multiline minRows={3} value={out} onFocus={()=> setActive('output')} onChange={e=> setOut(e.target.value)} fullWidth size="small" placeholder={"Your output should be a direct response.\n\n{agent_end}"}
              onDragOver={(e)=> e.preventDefault()}
              onDrop={(e)=>{ e.preventDefault(); const t = e.dataTransfer.getData('text/token') || e.dataTransfer.getData('text/plain'); if (t) { setActive('output'); insert(t); } }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={doSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};
